import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { getDocContext as getDocCtx, readAll as readAllDoc, resolveSpan as resolveSpanFacade, replaceWithMarkdown as replaceMarkdown, insert as insertViaFacade, remove as removeJson } from "../../services/doc/docApi";
import { DocumentUtils } from "../../services/doc/internal/yDocHighLevelAPI";
import { traceLog } from '../../../../services/debug/traceHelpers';
import { traceService } from '../../../../services/debug/traceService';
import { marked } from 'marked'; // ✅ FIX: Use ES6 import like yDocLLMAPI.ts
import katex from 'katex';
import { docsCRDTService } from "../../services/crdt/docsCRDTService";
import { parseMarkdownToTokens } from "../../../../shared/config/markedConfig";
import { NODE_TYPES, TOKEN_TYPES } from "../../types/nodeTypes";
import type { ContentArray, TextContentItem } from "../../types/contentItem";
import { hasText } from "../../types/contentItem";
import { createFileLogger } from "utils/logger";
import { generateGrokImage, searchAndDownloadUnsplash } from '../../../../services/imageService';

const log = createFileLogger('DOC_WRITE', { enabled: false });
/**
 * DEBUG MODE FLAG
 * Set to true to enable verbose markdown pipeline logging
 * Set to false for cleaner production logs
 */
const DEBUG_MARKDOWN_PIPELINE = false;

/**
 * 🔧 CUSTOM LATEX EXTRACTOR
 * Extracts LaTeX from markdown BEFORE marked.js processing
 * Replaces LaTeX with unique placeholders, then restores them as math nodes after parsing
 */
interface LatexMatch {
  placeholder: string;  // The unique placeholder ID (e.g., "⟦LATEX_DISPLAY_0⟧")
  latex: string;        // The LaTeX content
  displayMode: boolean; // true for $$, false for $
}

function extractLatexFromMarkdown(markdown: string): { cleaned: string; matches: LatexMatch[] } {
  // 🔧 DEFENSIVE: Validate input
  if (!markdown || typeof markdown !== 'string') {
    console.warn('[LATEX_EXTRACTOR] Invalid markdown input:', { type: typeof markdown });
    return { cleaned: '', matches: [] };
  }
  
  // 🔧 SECURITY: Prevent DOS with extremely large inputs
  if (markdown.length > 1000000) {
    console.error('[LATEX_EXTRACTOR] Markdown too large (>1MB), skipping LaTeX extraction');
    return { cleaned: markdown, matches: [] };
  }
  
  const matches: LatexMatch[] = [];
  let counter = 0;
  let cleaned = markdown;
  
  
  // 🔧 PREPROCESSING: Handle escaped dollar signs
  // Replace \$ with a temporary placeholder to prevent LaTeX extraction
  const escapedDollarPlaceholder = '⟦ESCAPED_DOLLAR⟧';
  cleaned = cleaned.replace(/\\\$/g, escapedDollarPlaceholder);
  
  // Extract display math $$...$$ (do this first to avoid conflicts with inline)
  // Match both inline format ($$formula$$) and multi-line format
  // CRITICAL: Use ⟦⟧ brackets instead of __ to avoid markdown stripping them!
  cleaned = cleaned.replace(/\$\$([\s\S]*?)\$\$/g, (match, content) => {
    const latex = content.trim();
    
    // 🔧 FIX: Skip empty LaTeX (just $$$$)
    if (!latex || latex.length === 0) {
      console.warn('⚠️ [LATEX_EXTRACTOR] Skipping empty display math');
      return '';  // Remove the $$$$ entirely
    }
    
    const placeholder = `⟦LATEX_DISPLAY_${counter++}⟧`;
    matches.push({
      placeholder,
      latex,
      displayMode: true
    });
    return placeholder;
  });
  
  // Extract inline math $...$
  // Use negative lookahead/lookbehind to avoid matching $$
  cleaned = cleaned.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)/g, (match, content) => {
    const latex = content.trim();
    
    // 🔧 FIX: Skip empty LaTeX (just $$)
    if (!latex || latex.length === 0) {
      console.warn('⚠️ [LATEX_EXTRACTOR] Skipping empty inline math');
      return '';  // Remove the $$ entirely
    }
    
    const placeholder = `⟦LATEX_INLINE_${counter++}⟧`;
    matches.push({
      placeholder,
      latex,
      displayMode: false
    });
    return placeholder;
  });
  
  // 🔧 POSTPROCESSING: Restore escaped dollar signs
  cleaned = cleaned.replace(new RegExp(escapedDollarPlaceholder, 'g'), '\\$');
  
  if (matches.length > 0) {
    console.log('🧮 [LATEX_EXTRACTOR] Extracted LaTeX:', {
      totalFormulas: matches.length,
      display: matches.filter(m => m.displayMode).length,
      inline: matches.filter(m => !m.displayMode).length
    });
  }
  
  return { cleaned, matches };
}

/**
 * Force flush document save after AI writes to ensure persistence before command returns.
 */
async function forceFlushDocumentSave(docId: string): Promise<void> {
  try {
    await docsCRDTService.forceSaveDocument(docId);
  } catch (error) {
    console.error(`[doc_write] Failed to save document ${docId.slice(-8)}:`, error);
  }
}

/**
 * Pre-fetch images in markdown by resolving grok: and unsplash: URL prefixes.
 * Replaces ![alt](grok:prompt) and ![alt](unsplash:query) with actual image URLs
 * before the markdown parsing pipeline runs.
 */
async function preFetchImagesInMarkdown(markdown: string): Promise<string> {
  // Also captures an optional {key=value ...} attribute suffix after the image.
  // The captured attrs are encoded into the title field as "IMGATTRS:key=value ..."
  // so they survive marked.js tokenization and can be applied to the Y.XmlElement.
  const imageRegex = /!\[([^\]]*)\]\((grok:|unsplash:)([^)]+)\)(\{([^}]*)\})?/g;
  const matches = [...markdown.matchAll(imageRegex)];
  if (matches.length === 0) return markdown;

  let result = markdown;
  for (const match of matches) {
    const [fullMatch, alt, prefix, text, attrBlock, attrContent] = match;
    const source = prefix === 'grok:' ? 'grok' : 'unsplash';
    console.log(`[doc_write] 🖼️ Fetching ${source} image:`, text.trim());
    try {
      let imageUrl: string;
      if (source === 'grok') {
        const res = await generateGrokImage(text.trim());
        imageUrl = res.url;
      } else {
        const res = await searchAndDownloadUnsplash(text.trim());
        imageUrl = res.url;
      }

      if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
        throw new Error(`Image service returned invalid URL: ${imageUrl}`);
      }
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('data:')) {
        throw new Error(`Image URL must start with http://, https://, or data: - got: ${imageUrl.substring(0, 50)}`);
      }

      console.log(`[doc_write] ✅ Image fetched:`, imageUrl.substring(0, 60) + '...');

      // Encode any image-layout attrs in the title field so marked.js carries them through.
      // yDocLLMAPI.ts will later parse the "IMGATTRS:..." prefix.
      const titleSuffix = attrContent ? ` "IMGATTRS:${attrContent.trim()}"` : '';
      result = result.replace(fullMatch, () => `![${alt}](${imageUrl}${titleSuffix})`);
    } catch (error: any) {
      throw new Error(`Failed to fetch ${source} image "${text.trim()}": ${error.message}`);
    }
  }
  return result;
}

type InsertLocation = { before?: string; after?: string } | { at: 'end' | 'start' };

/**
 * 🧹 MARKDOWN NORMALIZATION MODULE
 * 
 * Cleans Claude's markdown input to prevent extra newlines from becoming empty paragraph nodes.
 * This is the FIRST step in the AI→Y.js conversion pipeline.
 */
export class MarkdownNormalizer {
  /**
   * COMPREHENSIVE MARKDOWN CLEANING
   * Removes all forms of extra newlines and whitespace that would create empty nodes
   */
  static normalize(markdown: string): {
    cleaned: string;
    issues: string[];
    stats: { leadingNewlines: number; trailingNewlines: number; doubleNewlines: number };
  } {
    if (!markdown || typeof markdown !== 'string') {
      return { cleaned: '', issues: ['Invalid markdown input'], stats: { leadingNewlines: 0, trailingNewlines: 0, doubleNewlines: 0 } };
    }

    const stats = {
      leadingNewlines: 0,
      trailingNewlines: 0,
      doubleNewlines: 0
    };
    const issues: string[] = [];

    // 🔍 PHASE 0: Analysis before cleaning
    const hasLeadingNewlines = /^\s*\n+/.test(markdown);
    const hasTrailingNewlines = /\n+\s*$/.test(markdown);
    if (hasLeadingNewlines) stats.leadingNewlines = (markdown.match(/^\n+/) || [])[0]?.length || 0;
    if (hasTrailingNewlines) stats.trailingNewlines = (markdown.match(/\n+$/) || [])[0]?.length || 0;

    let cleaned = markdown;

    // 🔍 PHASE 1: Remove leading and trailing NEWLINES (but preserve horizontal whitespace/indentation)
    // 🚨 CRITICAL: This prevents markdown like "\n\n\nContent" from creating empty paragraphs
    // ✅ FIX: Only remove vertical whitespace (\n, \r), NOT horizontal (\t, space)
    // This preserves indentation on the first line of content
    cleaned = cleaned.replace(/^[\r\n]+/, '').replace(/[\r\n\s]+$/, '');
    if (cleaned !== markdown) {
      issues.push('Removed leading/trailing whitespace');
    }

    // 🔍 PHASE 2: Normalize carriage returns and line endings
    cleaned = cleaned
      .replace(/\r\n/g, '\n')  // Windows line endings → Unix
      .replace(/\r/g, '\n');   // Old Mac line endings → Unix

    // 🔍 PHASE 3: Collapse multiple newlines (3+) to exactly 2 newlines
    // This is the SAFE maximum - two newlines = one blank line between paragraphs
    const tripleNewlineCount = (cleaned.match(/\n{3,}/g) || []).length;
    stats.doubleNewlines = tripleNewlineCount;
    if (tripleNewlineCount > 0) {
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
      issues.push(`Normalized ${tripleNewlineCount} triple+ newlines to double newlines`);
    }

    // 🔍 PHASE 4: Fix malformed headings (no space after #)
    // Convert "#Heading" → "# Heading" to ensure marked.lexer parses correctly
    cleaned = cleaned.replace(/^(#{1,6})([^\s#])/gm, '$1 $2');
    
    // 🔍 PHASE 4.5: Ensure headings are isolated on their own lines
    // If text immediately follows heading without blank line, add one
    // Example: "# Heading\nText" → "# Heading\n\nText"
    cleaned = cleaned.replace(/^(#{1,6}\s+[^\n]+)\n(?=[^\n#])/gm, '$1\n\n');

    // 🔍 PHASE 4.6: Fix newlines WITHIN list items and other block structures
    // These patterns fix cases like: "- item1\n\n- item2" → "- item1\n- item2"
    cleaned = cleaned
      // Don't add newlines between list items
      .replace(/^(-|\*|\d+\.)\s+([^\n]+)\n\n(?=(-|\*|\d+\.)\s+)/gm, '$1 $2\n')
      // 🔴 FIX: Only collapse 3+ newlines, PRESERVE legitimate paragraph breaks (2 newlines)!
      // OLD (WRONG): /(\S)\n\n+(?=\S)/g - collapsed ALL double+newlines
      // NEW (RIGHT): /(\S)\n{3,}(?=\S)/g - only collapses 3+ newlines, keeps 2
      .replace(/(\S)\n{3,}(?=\S)/g, '$1\n\n');

    // 🔍 PHASE 5: Ensure single newlines between distinct blocks
    // If we have: "text\ntext" (missing blank line between logical blocks), preserve it
    // But "text\n\n\ntext" should become "text\n\ntext"
    // This is already handled by PHASE 3

    // 🔍 PHASE 6: Strip excessive whitespace on lines
    cleaned = cleaned
      .split('\n')
      .map((line, lineIndex) => {
        const hasLeadingSpaces = /^\s+/.test(line);
        const leadingSpaceCount = hasLeadingSpaces ? line.match(/^\s+/)?.[0].length || 0 : 0;
        
        // Preserve intentional indentation in code blocks and lists
        if (line.match(/^(\s{2,}|-|\*|\d+\.|#{1,6}\s|```|>)/)) {
          // For lists and other structures, just trim trailing whitespace
          const result = line.trimEnd();
          
          if (hasLeadingSpaces) {
            console.log('🔍 [INDENT_DEBUG] [Normalizer] Structural line preserved:', {
              lineIndex,
              original: JSON.stringify(line.substring(0, 50)),
              leadingSpaces: leadingSpaceCount,
              result: JSON.stringify(result.substring(0, 50)),
              preserved: leadingSpaceCount === (result.match(/^\s+/)?.[0].length || 0)
            });
          }
          
          return result;
        }
        // ✅ FIX: For normal text, only trim trailing whitespace and collapse EXCESSIVE internal spaces (3+)
        // CRITICAL: Don't collapse leading spaces (indentation), only collapse internal runs
        // Preserve: "    Indented text" (leading indent)
        // Preserve: "Name:  Value" (2 spaces for alignment)
        // Collapse: "Name:     Value" (5 internal spaces) → "Name:  Value" (2 spaces)
        const result = line
          .replace(/\s+$/, '')  // Trim trailing
          .replace(/(\S)\s{3,}(?=\S)/g, '$1  ');  // Collapse 3+ spaces BETWEEN non-whitespace (not at start!)
        
        if (hasLeadingSpaces) {
          console.log('🔍 [INDENT_DEBUG] [Normalizer] Normal line processed:', {
            lineIndex,
            original: JSON.stringify(line.substring(0, 50)),
            leadingSpaces: leadingSpaceCount,
            result: JSON.stringify(result.substring(0, 50)),
            preserved: leadingSpaceCount === (result.match(/^\s+/)?.[0].length || 0)
          });
        }
        
        return result;
      })
      .join('\n');

    if (issues.length > 0) {
      console.log('🧹 [MARKDOWN_NORMALIZER] Fixed issues:', {
        count: issues.length,
        issues: issues.slice(0, 3)
      });
    }
    
    return { cleaned, issues, stats };
  }

  /**
   * Filter tokens to remove empty ones
   * This works AFTER marked.lexer creates tokens
   */
  static filterEmptyTokens(tokens: any[]): { filtered: any[]; removedCount: number; removedTypes: string[] } {
    const removedTypes = new Set<string>();
    let removedCount = 0;

    const filtered = tokens.filter((token: any) => {
      // Remove space tokens
      if (token.type === TOKEN_TYPES.SPACE) {
        removedTypes.add(TOKEN_TYPES.SPACE);
        removedCount++;
        return false;
      }

      // 🚨 CRITICAL: Remove empty paragraph tokens
      // These occur when marked.lexer parses multiple consecutive newlines
      if (token.type === TOKEN_TYPES.PARAGRAPH && (!token.text || token.text.trim() === '')) {
        removedTypes.add('empty-paragraph');
        removedCount++;
        return false;
      }

      // Remove paragraphs containing only whitespace/newlines
      if (token.type === TOKEN_TYPES.PARAGRAPH && token.text && /^\s+$/.test(token.text)) {
        removedTypes.add('whitespace-paragraph');
        removedCount++;
        return false;
      }

      // Remove other empty blocks
      if (!token.text && !token.tokens && !token.items && !token.header && !token.rows && token.type !== 'hr' && token.type !== 'thematicBreak') {
        removedTypes.add(`empty-${token.type}`);
        removedCount++;
        return false;
      }

      return true;
    });

    return { filtered, removedCount, removedTypes: Array.from(removedTypes) };
  }

  /**
   * Validate that content items don't have empty text
   * This works AFTER contentArray is built from tokens
   */
  static validateContentArray(contentArray: any[]): { validated: any[]; removedCount: number } {
    let removedCount = 0;

    const validated = contentArray.filter((item: any) => {
      // Remove paragraphs with empty or whitespace-only text
      if (item.type === 'paragraph' && (!item.text || item.text.trim() === '')) {
        removedCount++;
        return false;
      }

      // Keep everything else
      return true;
    });

    return { validated, removedCount };
  }
}

/**
 * ✅ ENHANCED: Generate contextual completion messages based on operation details
 */
function generateContextualMessage(params: {
  operation: 'replace' | 'insert' | 'remove';
  nodeCount: number;
  markdownContent?: string;
  originalStructureInfo?: any;
  bubbleText?: string;
  newNodes?: any[];
}): string {
  const { operation, nodeCount, markdownContent, originalStructureInfo, bubbleText, newNodes } = params;
  
  // Analyze content to generate contextual descriptions
  const contentAnalysis = analyzeContentForMessage(markdownContent, originalStructureInfo, newNodes);
  
  switch (operation) {
    case 'replace':
      if (nodeCount === 1) {
        if (contentAnalysis.isListStructure) {
          return `Updated ${contentAnalysis.listType} with ${contentAnalysis.itemCount} items`;
        } else if (contentAnalysis.hasHeading) {
          return `Revised section: "${contentAnalysis.headingText}"`;
        } else {
          return `Updated paragraph${contentAnalysis.contentPreview ? ` - ${contentAnalysis.contentPreview}` : ''}`;
        }
      } else {
        if (contentAnalysis.isListStructure) {
          return `Restructured ${nodeCount} sections into ${contentAnalysis.listType} with ${contentAnalysis.itemCount} items`;
        } else if (contentAnalysis.hasMultipleHeadings) {
          return `Updated ${nodeCount} sections including: ${contentAnalysis.headingsList}`;
        } else {
          return `Revised ${nodeCount} sections${contentAnalysis.contentPreview ? ` - ${contentAnalysis.contentPreview}` : ''}`;
        }
      }
      
    case 'insert':
      if (contentAnalysis.isListStructure) {
        return `Added ${contentAnalysis.listType} with ${contentAnalysis.itemCount} items`;
      } else if (contentAnalysis.hasHeading) {
        return `Inserted new section: "${contentAnalysis.headingText}"`;
      } else {
        return `Added content${contentAnalysis.contentPreview ? ` - ${contentAnalysis.contentPreview}` : ''}`;
      }
      
    case 'remove':
      return `Removed ${nodeCount} section${nodeCount > 1 ? 's' : ''}`;
      
    default:
      return `Completed ${operation} operation on ${nodeCount} section${nodeCount > 1 ? 's' : ''}`;
  }
}

/**
 * ✅ ENHANCED: Analyze content to extract meaningful descriptors for messages
 */
function analyzeContentForMessage(markdownContent?: string, originalStructureInfo?: any, newNodes?: any[]): {
  isListStructure: boolean;
  listType?: 'ordered list' | 'bullet list';
  itemCount?: number;
  hasHeading: boolean;
  headingText?: string;
  hasMultipleHeadings: boolean;
  headingsList?: string;
  contentPreview?: string;
} {
  const analysis = {
    isListStructure: false,
    hasHeading: false,
    hasMultipleHeadings: false,
  } as any;
  
  if (!markdownContent) return analysis;
  
  // Detect list structures
  const orderedListItems = markdownContent.match(/^\d+\.\s+/gm);
  const bulletListItems = markdownContent.match(/^[-*]\s+/gm);
  
  if (orderedListItems && orderedListItems.length > 0) {
    analysis.isListStructure = true;
    analysis.listType = 'ordered list';
    analysis.itemCount = orderedListItems.length;
  } else if (bulletListItems && bulletListItems.length > 0) {
    analysis.isListStructure = true;
    analysis.listType = 'bullet list';
    analysis.itemCount = bulletListItems.length;
  }
  
  // Detect headings
  const headings = markdownContent.match(/^#+\s+(.+)$/gm);
  if (headings && headings.length > 0) {
    analysis.hasHeading = true;
    analysis.headingText = headings[0].replace(/^#+\s+/, '').trim();
    
    if (headings.length > 1) {
      analysis.hasMultipleHeadings = true;
      analysis.headingsList = headings
        .slice(0, 3) // Limit to first 3 headings
        .map(h => h.replace(/^#+\s+/, '').trim())
        .join(', ') + (headings.length > 3 ? '...' : '');
    }
  }
  
  // Generate content preview (first meaningful line)
  if (!analysis.isListStructure && !analysis.hasHeading) {
    const meaningfulLines = markdownContent
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 10 && !line.startsWith('#') && !line.match(/^\d+\.\s+/) && !line.match(/^[-*]\s+/));
    
    if (meaningfulLines.length > 0) {
      analysis.contentPreview = meaningfulLines[0].substring(0, 60) + (meaningfulLines[0].length > 60 ? '...' : '');
    }
  }
  
  return analysis;
}

export const docWriteCommand: SimpleDocCommand = {
  id: 'doc_write',
  description: `Replace, insert, or remove content in Documents.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- For Notes use note_write; for Pages use page_write

**OPERATIONS:**
- replace: Change existing content (requires selector with nodes or range + content + reasoning)
- insert: Add new content (requires selector with positioning + content + reasoning)
- remove: Delete content (requires selector with nodes or range + reasoning)

Max 1,000 words per operation. For larger changes, make sequential calls (500-1,000 words each) and use returned node IDs to position next section.

**WHOLE-DOC REPLACE:** Use selector {at: "all"} to target the entire document. For content ≤1,000 words: single replace call. For larger content: remove with {at: "all"} first, then insert in sequential chunks using {at: "start"} followed by {after: "<last_inserted_id>"}.

**INDENTATION:**
Use actual TAB character (U+0009), NOT "\t" literal or 4 spaces. Example: \tIndented text

**CODE BLOCKS:**
MUST use triple backticks: \`\`\`javascript\\ncode\\n\`\`\`. Four spaces creates indented paragraph, not code.

**HTML STYLING:**
Use <span style="..."> for visual styling. Supported: color (red | #ff0000 | rgb()), background-color, font-family (Arial | monospace), font-size (16pt | 20px, clamped 6-72pt), font-weight (bold | 700), font-style (italic), text-decoration (underline | line-through), vertical-align (super | sub). Use explicit values only (no inherit/initial). Combine: <span style="color: red; font-size: 18pt">Text</span>. With markdown: <span style="color: red">**bold**</span>. Nested: <span style="color: blue">Blue <span style="font-size: 18pt">large</span></span>. With indentation: \\t<span style="color: red">Indented</span>.

**STYLE INHERITANCE:**
fontFamily, fontSize, textColor inherit from previous paragraph automatically. Override explicitly (don't use "inherit" keyword).

**IMAGES:**
Inline image generation using special URL prefixes in standard markdown image syntax:

AI-generated (Grok): ![alt text](grok:detailed prompt description)
Stock photo (Unsplash): ![alt text](unsplash:search query)

Example: ![Mountain lake at sunset](unsplash:mountain lake sunset)
Example: ![Futuristic city](grok:cyberpunk cityscape with neon lights at night)`,
  logicalName: 'doc_write',
  version: 'v2',
  appType: 'docs',
  autonomyLevel: 'auto',  // ✅ FIX: Execute automatically, use document-level Accept all/Reject all buttons
  visualTreatment: 'demonstrative',
  
  // ✅ NEW: Structured view data for guaranteed persistence and consistent rehydration
  formatViewData: (result: any, toolInput: any) => {
    try {
      // For document operations, return structured view data
      if (result?.data) {
        const data = result.data;
        
        // Document operation types: replace, insert, remove
        if (data.type === 'document_replace' || data.type === 'document_insert_markdown' || data.type === 'document_remove' || data.type === 'document_insert') {
          return {
            type: 'document_operation',
            summary: result.message || result.summary || 'Document updated',
            data: {
              operation: data.type.replace('document_', '').replace('_markdown', ''),
              nodeCount: data.replacedCount || data.insertedNodeIds?.length || data.insertedCount || data.removedCount || 0,
              reasoning: toolInput?.reasoning,  // AI's explanation and word count
              fullContent: data.messageContext?.fullContent, // ← Full generated content
              contentPreview: data.messageContext?.contentPreview, // ← Short preview for lists
              contextWindow: data.contextWindow ? {
                text: data.contextWindow.text?.substring(0, 500),
                nodeIds: data.contextWindow.nodeIds?.slice(0, 10),
                fromId: data.contextWindow.fromId,
                toId: data.contextWindow.toId
              } : undefined,
              revisionId: data.revisionId,
              documentId: toolInput?.id,  // For navigation
              insertedNodeIds: data.insertedNodeIds  // For scrolling to location
            },
            meta: {
              duration: result.metadata?.executionTime
            }
          };
        }
      }
      
      // Fallback for unexpected result format
      return {
        type: 'tool_summary',
        summary: result?.message || result?.summary || 'Updated document',
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[DOC_WRITE_V2] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Updated document',
        data: {}
      };
    }
  },
  
  // ✅ ENHANCED: Custom result formatter with rich preview support (DEPRECATED - use formatViewData)
  formatResultPreview: (result: any, toolInput: any) => {
    try {
      // For expandable view, return structured preview object
      if (result?.data) {
        const data = result.data;
        
        // Create rich preview for document operations
        if (data.type === 'document_replace' || data.type === 'document_insert_markdown' || data.type === 'document_remove') {
          const preview = {
            type: 'document_operation',
            summary: result.message || result.summary || 'Document updated',
            operation: data.type.replace('document_', ''),
            details: {
              nodeCount: data.replacedCount || data.insertedNodeIds?.length || data.removedCount || 0,
              revisionId: data.revisionId,
              contextWindow: data.contextWindow ? {
                beforeText: data.contextWindow.text?.substring(0, 200) + '...',
                nodeIds: data.contextWindow.nodeIds?.slice(0, 5)
              } : undefined,
              messageContext: data.messageContext
            }
          };
          
          // Return structured object for expandable view
          return preview;
        }
      }
      
      // For non-expandable view, return simple string
      // Prefer StandardToolResult.message (contains our enhanced contextual message)
      if (result && typeof result.message === 'string' && result.message.length > 0) {
        return result.message;
      }
      
      // Fallback to summary field
      if (result && typeof result.summary === 'string' && result.summary.length > 0) {
        return result.summary;
      }
      
      // Try to construct from data if available
      if (result?.data) {
        const data = result.data;
        if (data.messageContext?.operationSummary) {
          const preview = data.messageContext.contentPreview ? ` - ${data.messageContext.contentPreview}` : '';
          return `${data.messageContext.operationSummary}${preview}`;
        }
        if (typeof data.replacedCount === 'number') return `Replaced ${data.replacedCount} section${data.replacedCount > 1 ? 's' : ''}`;
        if (typeof data.removedCount === 'number') return `Removed ${data.removedCount} section${data.removedCount > 1 ? 's' : ''}`;
        if (Array.isArray(data.insertedNodeIds)) return `Inserted ${data.insertedNodeIds.length} section${data.insertedNodeIds.length > 1 ? 's' : ''}`;
      }
      
      // Last resort
      return 'Updated document';
      
    } catch (error) {
      console.warn('[DOC_WRITE_V2_FORMATTER] Error in formatResultPreview:', error);
      return 'Updated document';
    }
  },

  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to edit (e.g. "doc_meadow_silver_compass" or "file_violet_whisper_galaxy")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'doc_write is for Documents only. Use entity_write for Notes/Sheets/Canvas/Slides.';
          }
          if (value.startsWith('pdf_') || value.startsWith('file_')) {
            return 'doc_write is for Documents only. PDFs are read-only.';
          }
          return null;
        }
      },
      {
        name: 'operation',
        type: 'string',
        description: 'Edit operation: "replace" (change existing), "insert" (add new), or "remove" (delete)',
        required: true,
        validator: (value: string) => {
          if (!['replace', 'insert', 'remove'].includes(value)) {
            return 'Operation must be "replace", "insert", or "remove"';
          }
          return null;
        }
      },
      {
        name: 'selector',
        type: 'object',
        description: `What to target:

REPLACE/REMOVE (existing content):
  {"nodes": ["node_abc_123", "node_def_456"]} - Specific nodes
  {"range": {"from": "node_abc_123", "to": "node_xyz_789"}} - Range between nodes
  {"at": "all"} - Entire document

INSERT (positioning):
  {"at": "start"} or {"at": "end"} - Document start/end
  {"before": "node_abc_123"} or {"after": "node_abc_123"} - Relative position

Use node IDs from doc_read_v2 or document context`,
        required: true,
        validator: (value: any) => {
          // ✅ CRITICAL FIX: Only validate basic structure here
          // Operation-specific validation happens in handler (where all params are available)
          if (!value || typeof value !== 'object') return 'Selector must be an object';
          if (Object.keys(value).length === 0) return 'Selector cannot be empty';
          return null;
        }
      },
      {
        name: 'reasoning',
        type: 'string',
        description: 'Brief one-sentence explanation of what you are doing in this specific change. MUST include both: (1) the explanation AND (2) estimated word count in parentheses. Format: "Explanation text (estimated X words)". Example: "Adding introduction section (estimated 450 words)" or "Replacing methodology section (estimated 600 words)".',
        required: true,
        defaultValue: 'Editing document'
      },
      {
        name: 'content',
        type: 'string',
        description: `Markdown content to insert/replace with. Required for "replace" and "insert" (not "remove"). Max 1,000 words. For larger changes, make multiple calls (500-1,000 words each).

IMAGES: Use special URL prefixes to generate or fetch images inline:
- AI-generated (Grok): ![alt text](grok:detailed prompt description)
- Stock photo (Unsplash): ![alt text](unsplash:search query)

Example: ![A mountain lake at sunset](unsplash:mountain lake sunset)
Example: ![Futuristic city](grok:cyberpunk cityscape with neon lights at night)`,
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          id: 'doc_meadow_silver_compass',
          operation: 'insert',
          selector: { at: 'start' },
          reasoning: 'Adding introduction section (estimated 650 words)',
          content: '# Introduction\n\nThis paper examines the relationship between...\n\n## Background\n\nPrevious research has shown...'
        },
        description: 'INSERT: Adding content with reasoning and word count estimate'
      },
      {
        input: { 
          id: 'doc_cascade_violet_helix',
          operation: 'replace',
          selector: { nodes: ['node-market-1', 'node-market-2', 'node-market-3'] },
          reasoning: 'Updating market analysis section (estimated 450 words)',
          content: '## Market Analysis\n\nTarget market shows 15% annual growth...\n\n### Competition\nKey competitors include Company A and Company B...'
        },
        description: 'REPLACE: Updating specific nodes with new content'
      },
      {
        input: {
          id: 'doc_telephone_guitar_zodiac',
          operation: 'insert',
          selector: { after: 'node-methods-3' },
          reasoning: 'Adding discussion section after methodology (estimated 550 words)',
          content: '## Discussion\n\nThese results indicate significant improvements...'
        },
        description: 'INSERT: Positioning new content after specific node'
      },
      {
        input: {
          id: 'doc_violet_whisper_galaxy',
          operation: 'remove',
          selector: { nodes: ['node-outdated-1', 'node-outdated-2'] },
          reasoning: 'Removing outdated information (2 nodes)'
        },
        description: 'REMOVE: Deleting specific nodes'
      },
      {
        input: {
          id: 'doc_meadow_silver_compass',
          operation: 'replace',
          selector: { at: 'all' },
          reasoning: 'Replacing entire document with new content (estimated 700 words)',
          content: '# New Title\n\nNew content here...'
        },
        description: 'WHOLE-DOC REPLACE: {at: "all"} replaces everything in one call (use when content fits within 1,000 words)'
      },
      {
        input: {
          id: 'doc_meadow_silver_compass',
          operation: 'remove',
          selector: { at: 'all' },
          reasoning: 'Clearing document before rewriting in chunks (removing all nodes)'
        },
        description: 'WHOLE-DOC REPLACE (large docs): Step 1 — remove {at: "all"}, then follow with insert {at: "start"} calls for each chunk'
      }
    ],
    
    bad: [
      {
        input: {
          id: 'doc_meadow_silver_compass',
          operation: 'insert',
          selector: { at: 'start' },
          content: '# Complete 5000 word document...[massive content]...'
        },
        description: 'BAD: Over 1,000 words in single call AND missing reasoning parameter'
      },
      {
        input: {
          id: 'doc_cascade_violet_helix',
          operation: 'replace',
          selector: { nodes: ['node-5', 'node-12', 'node-3', 'node-18'] },
          reasoning: 'Updating scattered nodes (estimated 300 words)',
          content: 'Random edits...'
        },
        description: 'BAD: Editing unrelated scattered nodes instead of logical sections'
      },
      {
        input: {
          id: 'doc_telephone_guitar_zodiac',
          operation: 'insert',
          selector: { at: 'end' },
          reasoning: 'Adding content',
          content: 'Some content...'
        },
        description: 'BAD: Reasoning missing word count estimate'
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    // Check if auto-accept changes is enabled (workflow mode)
    const autoAcceptChanges = (context as any).autoAcceptChanges === true;
    const useSuggestionMode = !autoAcceptChanges;  // Disable suggestions when auto-accepting

    const startTime = Date.now();
    const traceId = context.traceId;
    const effectiveTraceId = traceId || `trace_${Date.now()}_${Math.random().toString(36).slice(2,9)}`;

    // ✅ CRITICAL FIX: Extract NEW parameter names (operation, selector, content) from updated schema
    const operation = params?.operation;
    const selector = params?.selector;
    const content = params?.content;
    const id = params?.entityId;
    const revisionId = params?.revisionId;
    const reasoning = params?.reasoning || 'Editing document';
    
    // Map new parameters to old internal variables for backwards compatibility
    // This allows existing code paths to work without massive refactoring
    const markdownContent = content;
    let insertLocation: any = undefined;
    let replaceNodes: any = undefined;
    let removeNodes: any = undefined;
    let fromNode: any = undefined;
    let toNode: any = undefined;
    const contentOptions: any = undefined; // Legacy parameter, no longer used
    const anchors: any = undefined; // Legacy parameter, no longer used
    
    // Parse selector based on operation type
    if (operation === 'insert' && selector) {
      // Insert operation: selector defines where to insert
      insertLocation = selector;
    } else if (operation === 'replace' && selector) {
      // Replace operation: selector defines what to replace
      if (selector.nodes) {  // ✅ FIX: Changed from selector.ids to selector.nodes (matches schema)
        replaceNodes = selector.nodes;
      } else if (selector.range) {  // ✅ FIX: Changed from selector.from/to to selector.range (matches schema)
        fromNode = selector.range.from;
        toNode = selector.range.to;
      }
    } else if (operation === 'remove' && selector) {
      // Remove operation: selector defines what to remove
      if (selector.nodes) {  // ✅ FIX: Changed from selector.ids to selector.nodes (matches schema)
        removeNodes = selector.nodes;
      } else if (selector.range) {  // ✅ FIX: Changed from selector.from/to to selector.range (matches schema)
        fromNode = selector.range.from;
        toNode = selector.range.to;
      }
    }

    // True when the AI passes selector: {at: "all"} for replace or remove
    const selectAll = (operation === 'replace' || operation === 'remove') && selector?.at === 'all';

    // Log AI's reasoning if provided
    if (reasoning && reasoning !== 'Editing document') {
      log.log(`[DOC_WRITE] AI Reasoning: ${reasoning}`);
    }

    log.log('🚨 [DOC_WRITE_HANDLER_DEBUG] === PARAMS EXTRACTED (NEW SCHEMA) ===', {
      timestamp: new Date().toISOString(),
      id,
      operation,
      selector,
      reasoning,
      hasContent: !!content,
      // Mapped to old variables:
      hasMarkdownContent: !!markdownContent,
      hasInsertLocation: !!insertLocation,
      insertLocation,
      replaceNodes,
      removeNodes,
      fromNode,
      toNode
    });

    // ✅ CRITICAL: Perform operation-specific validation now that all params are available
    // Schema validators only check basic structure; here we validate operation requirements
    if (operation === 'insert') {
      if (!selector.at && !selector.before && !selector.after) {
        return createErrorResult(
          'Invalid insert operation',
          'Selector must have "at", "before", or "after" property',
          'validation'
        );
      }
      if (selector.at === 'all') {
        return createErrorResult(
          'Invalid insert operation',
          '{at: "all"} is not valid for insert. Use {at: "start"}, {at: "end"}, {before: "node_id"}, or {after: "node_id"}.',
          'validation'
        );
      }
      if (!content || content.trim().length === 0) {
        return createErrorResult(
          'Invalid insert operation',
          'Content is required for insert operations',
          'validation'
        );
      }
    } else if (operation === 'replace') {
      if (!selector.nodes && !selector.range && selector.at !== 'all') {
        return createErrorResult(
          'Invalid replace operation',
          'Selector must have "nodes", "range", or {at: "all"} property',
          'validation'
        );
      }
      if (!content || content.trim().length === 0) {
        return createErrorResult(
          'Invalid replace operation',
          'Replace operation requires content (you cannot replace with empty string). If you want to delete nodes without replacing them, use operation: "remove" instead.',
          'validation'
        );
      }
    } else if (operation === 'remove') {
      if (!selector.nodes && !selector.range && selector.at !== 'all') {
        return createErrorResult(
          'Invalid remove operation',
          'Selector must have "nodes", "range", or {at: "all"} property',
          'validation'
        );
      }
    }

    try {
      // 🆕 NEW: ALWAYS clean up any streaming placeholder FIRST
      try {
        const { doc: preCleanupDoc, error: preCleanupError } = await getDocCtx(id, context);
        if (preCleanupDoc) {
          // 🔴 CRITICAL: Log state BEFORE snapshot
          const yxmlLengthBefore = preCleanupDoc.yxml.length;
          log.log(`🔴 [PRE_CLEANUP_START] === PLACEHOLDER CLEANUP START ===`, {
            timestamp: new Date().toISOString(),
            yxmlLengthBefore,
            willScanForPlaceholders: true
          });
          
          const yxmlArray = preCleanupDoc.yxml.toArray();
          log.log(`🔴 [SNAPSHOT_CREATED] Created snapshot of yxml`, {
            snapshotLength: yxmlArray.length,
            yxmlLiveLength: preCleanupDoc.yxml.length,
            match: yxmlArray.length === preCleanupDoc.yxml.length
          });
          
          const placeholders = yxmlArray.reduce((acc: number[], el: any, idx: number) => {
            if (el && typeof el.getAttribute === 'function' && el.nodeName) {
              const elemId = el.getAttribute('id');
              const isFailed = el.getAttribute('data-streaming-failed') === 'true';
              const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
              
              // Only delete if it's an active streaming placeholder or failed
              // Don't delete committed content that just happens to have a streaming_ ID
              if (isActiveStreaming || isFailed) {
                acc.push(idx);
              }
            }
            return acc;
          }, []).sort((a, b) => b - a);  // Delete from end first
          
          log.log(`🔴 [PLACEHOLDERS_FOUND] Found placeholders to delete`, {
            count: placeholders.length,
            indices: placeholders,
            willDeleteInReverseOrder: true,
            expectedAfterLength: yxmlLengthBefore - placeholders.length
          });
          
          if (placeholders.length > 0) {
            log.log(`🧹 [DOC_WRITE_PRE_CLEANUP] Found ${placeholders.length} streaming placeholder(s), removing`, {
              placeholders,
              toolUseId: context.executionId
            });
            
            // 🔴 CRITICAL: Track deletion progress
            preCleanupDoc.yDoc.transact(() => {
              placeholders.forEach((idx, deleteOrder) => {
                log.log(`🔴 [DELETING_PLACEHOLDER] About to delete placeholder`, {
                  deleteOrder,
                  indexToDelete: idx,
                  yxmlLengthNow: preCleanupDoc.yxml.length,
                  willBeAfterLength: preCleanupDoc.yxml.length - 1
                });
                
                preCleanupDoc.yxml.delete(idx, 1);
                
                log.log(`✅ [PLACEHOLDER_DELETED] Placeholder deleted`, {
                  deletedIndex: idx,
                  yxmlLengthAfter: preCleanupDoc.yxml.length,
                  remainingToDelete: placeholders.length - deleteOrder - 1
                });
              });
              log.log(`✅ [DOC_WRITE_PRE_CLEANUP] ${placeholders.length} placeholder(s) deleted`);
            }, { source: 'tool-execution-cleanup', toolUseId: context.executionId });
            
            // 🔴 CRITICAL: Verify deletion worked
            const yxmlLengthAfter = preCleanupDoc.yxml.length;
            const expectedLength = yxmlLengthBefore - placeholders.length;
            log.log(`🔴 [POST_CLEANUP_VERIFICATION] Cleanup complete, verifying result`, {
              yxmlLengthBefore,
              placeholdersDeleted: placeholders.length,
              yxmlLengthAfter,
              expectedLength,
              deletionSuccessful: yxmlLengthAfter === expectedLength,
              discrepancy: yxmlLengthAfter - expectedLength,
              timestamp: new Date().toISOString()
            });
            
            // 🔥 CRITICAL: If deletion failed, log the remaining placeholders!
            if (yxmlLengthAfter !== expectedLength) {
              console.error(`🔥 [DELETION_FAILED] Placeholders not fully deleted!`, {
                expected: expectedLength,
                actual: yxmlLengthAfter,
                missing: yxmlLengthAfter - expectedLength,
                possibleReasons: ['stale_indices', 'deletion_order_wrong', 'elements_not_removed']
              });
              
              // Scan remaining placeholders
              const remainingPlaceholders = [];
              for (let i = 0; i < preCleanupDoc.yxml.length; i++) {
                const el = preCleanupDoc.yxml.get(i);
                if (el && typeof el.getAttribute === 'function' && (el as any).nodeName) {
                  const elemId = el.getAttribute('id');
                  if (elemId && elemId.startsWith('streaming_')) {
                    remainingPlaceholders.push({ index: i, id: elemId });
                  }
                }
              }
              console.error(`🔥 [REMAINING_PLACEHOLDERS] Still found placeholders after cleanup`, {
                count: remainingPlaceholders.length,
                placeholders: remainingPlaceholders
              });
            }
          } else {
            log.log(`ℹ️ [NO_PLACEHOLDERS] No streaming placeholders found to clean up`, {
              yxmlLength: yxmlLengthBefore
            });
          }
        }
      } catch (preCleanupError) {
        console.error(`❌ [DOC_WRITE_PRE_CLEANUP_ERROR] Error cleaning placeholders:`, {
          error: preCleanupError instanceof Error ? preCleanupError.message : String(preCleanupError)
        });
        // Continue despite cleanup error
      }

      try { context.onProgress?.({ type: 'streaming_update', activity: 'Writing to document...' }); } catch {}
      
      // 🏗️ NEW: Check if we're in reference-only mode
      const hasDocumentReferences = (context as any).documentReferences && Array.isArray((context as any).documentReferences);
      const backendMustFetchFresh = (context as any).contentFreshness?.backendMustFetchFresh;
      
      log.log(`🔍 [DOC_WRITE_DEBUG] Reference-only mode check:`, {
        hasDocumentReferences,
        backendMustFetchFresh
      });
      
      if (hasDocumentReferences && backendMustFetchFresh) {
        log.log(`🏗️ [REFERENCE_ONLY] doc_write in reference-only mode:`, {
          documentId: id,
          documentRefsCount: (context as any).documentReferences.length,
          explicitContentCount: ((context as any).explicitContent || []).length,
          freshContentAlreadyFetched: 'prompt_assembly_phase',
          timestamp: new Date().toISOString()
        });
        
        // ✅ FRESH CONTENT NOW FETCHED IN PROMPT ASSEMBLY - no need to fetch again!
        log.log(`✅ [REFERENCE_ONLY] Fresh content already available from prompt assembly phase - proceeding with guaranteed fresh node IDs`);
      }
      
      traceService.log({
        traceId: effectiveTraceId,
        type: 'tool_invocation',
        data: {
          tool: 'doc_write',
          referenceOnlyMode: hasDocumentReferences && backendMustFetchFresh,
          params: {
            id,
            hasMarkdownContent: !!markdownContent,
            hasContentOptions: Array.isArray(contentOptions) ? contentOptions.length : undefined,
            hasReplaceNodes: Array.isArray(replaceNodes) ? replaceNodes.length : undefined,
            hasRemoveNodes: Array.isArray(removeNodes) ? removeNodes.length : undefined,
            hasInsertLocation: !!insertLocation
          }
        }
      });
      
      if (!id) {
        return createErrorResult('Missing document ID', 'id parameter is required', 'validation');
      }

      log.log(`📊 [DOC_WRITE_DEBUG] Starting document context retrieval for:`, {
        id,
        hasContext: !!context,
        referenceOnlyMode: hasDocumentReferences && backendMustFetchFresh,
        guaranteedFresh: (context as any).guaranteedFresh || false,
        timestamp: new Date().toISOString()
      });
      
      const { doc, error } = await getDocCtx(id, context);
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Processing markdown content...' }); } catch {}
      if (error || !doc) {
        console.error(`❌ [DOC_WRITE_DEBUG] Document access failed:`, {
          id,
          error: error || 'Y.js document not accessible',
          hasDoc: !!doc,
          docProperties: doc ? Object.keys(doc) : null,
          timestamp: new Date().toISOString()
        });
        return createErrorResult('Document not available', error || 'Y.js document not accessible', 'validation');
      }
      
      // 🚨 ENHANCED: Verify Y.js document is properly accessible
      log.log(`📊 [DOC_WRITE_DEBUG] Document context verified:`, {
        id,
        hasDoc: !!doc,
        hasYxml: !!doc.yxml,
        hasYDoc: !!doc.yDoc,
        docId: doc.docId,
        windowId: doc.windowId,
        yxmlLength: doc.yxml?.length || 0,
        timestamp: new Date().toISOString()
      });
      
      // 🔍 DEBUG: Log Y.Doc instance ID for correlation with CRDT observer
      log.log(`🔍 [DOC_WRITE_YDOC_INSTANCE] ${doc.docId.slice(-8)} - Using Y.Doc:`, {
        clientID: doc.yDoc?.clientID,
        guid: doc.yDoc?.guid,
        timestamp: new Date().toISOString(),
        note: 'Compare with OBSERVER_ATTACHED log - clientIDs must match!'
      });

      traceService.log({
        traceId: effectiveTraceId,
        type: 'doc_context_ready',
        data: { tool: 'doc_write', docId: doc.docId, windowId: (doc as any).windowId }
      });

      // Attach traceId for downstream tracing
      (doc as any).metadata = { ...(doc as any).metadata, traceId };

      // Helper: full-doc span (kept internal, but not exposed via 'ALL' token)
      const resolveAllSpan = () => {
        const all = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        if (!all.nodes || all.nodes.length === 0) return null;
        return { fromId: all.nodes[0].id, toId: all.nodes[all.nodes.length - 1].id, rangeIds: all.nodes.map((n: any) => n.id) } as const;
      };

      // Helper: check if document is minimal (empty or nearly empty)
      const isDocumentMinimal = () => {
        const all = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const contentNodes = (all.nodes || []).filter((n: any) => 
          n.type !== 'page' && 
          n.type !== 'document' && 
          n.text && 
          n.text.trim().length > 0
        );

        // 🔍 DEBUG: Log initial document structure analysis
        log.log('🔍 [DOCUMENT_STRUCTURE_DEBUG] === INITIAL DOCUMENT STATE ANALYSIS ===', {
          timestamp: new Date().toISOString(),
          totalNodes: all.nodes?.length || 0,
          allNodeTypes: (all.nodes || []).map((n: any) => ({ type: n.type, id: n.id, hasText: !!(n.text && n.text.trim()) })),
          pageNodes: (all.nodes || []).filter((n: any) => n.type === 'page'),
          contentNodes: contentNodes,
          documentStructure: {
            hasPages: (all.nodes || []).some((n: any) => n.type === 'page'),
            pageCount: (all.nodes || []).filter((n: any) => n.type === 'page').length,
            blockCount: (all.nodes || []).filter((n: any) => n.type !== 'page' && n.type !== 'document').length,
            contentCount: contentNodes.length
          },
          isMinimal: contentNodes.length <= 2
        });

        return contentNodes.length <= 2;
      };

      // ✅ FIXED: Use hierarchical reading to handle page-aware insertion
      const getFirstPageInsertionPoint = (): any => {
        // Read document with page-aware hierarchical structure
        const docContext = { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any;
        const result = readAllDoc(docContext);
        const structure = result.nodes || [];
        
        if (!structure || !structure.length) {
          // Fresh document: signal caller to perform full-document replacement (preserves Markdown semantics)
          return null as any;
        }

        // Find the first page
        const firstPage = structure.find((node: any) => node.type === 'page');
        if (!firstPage) {
          // No explicit page nodes (brand-new doc): prefer full replacement for proper heading/list structure
          return null as any;
        }

        // Get content nodes inside the first page
        const page1ContentNodes = structure.filter((n: any) => n.parentPageId === firstPage.id);
        
        if (page1ContentNodes.length > 0) {
          // Check for non-empty content
          const nonEmptyNodes = page1ContentNodes.filter((n: any) => 
            n.text && n.text.trim().length > 0
          );
          
          if (nonEmptyNodes.length > 0) {
            // Insert after the last content node inside page 1
            const lastContentInPage1 = nonEmptyNodes[nonEmptyNodes.length - 1];
            return { after: lastContentInPage1.id };
          } else {
            // All content is empty paragraphs - replace first empty paragraph
            const firstEmptyParagraph = page1ContentNodes.find((n: any) => 
              n.type === 'paragraph'
            );
            
            // Return replacement spec to eliminate blank line
            return firstEmptyParagraph ? 
              { replace: { from: firstEmptyParagraph.id, to: firstEmptyParagraph.id } } : 
              { after: firstPage.id };
          }
        } else {
          // Page 1 is empty - insert at start of page
          return { after: firstPage.id };
        }
      };

      // Helper: convert markdown to content array format for Insert command
      const markdownToContentArray = (markdown: string): ContentArray => {
        // Debug logging (controlled by DEBUG_MARKDOWN_PIPELINE flag)
        if (DEBUG_MARKDOWN_PIPELINE) {
          console.log('🔍 [MARKED_DEBUG] === MARKED LIBRARY INVESTIGATION ===', {
            timestamp: new Date().toISOString(),
            markdownInput: markdown,
            markdownLength: markdown.length
          });
          
          console.log('🔍 [MARKED_DEBUG] Marked library details (ES6 import):', {
            markedType: typeof marked,
            markedConstructor: marked.constructor?.name,
            markedKeys: Object.keys(marked),
            hasLexer: 'lexer' in marked,
            lexerType: typeof marked.lexer,
            hasParse: 'parse' in marked,
            parseType: typeof marked.parse,
            markedVersion: (marked as any).version || (marked as any).VERSION || 'unknown'
          });
        }
        
        // 🔍 TAB_DEBUG: Check for indentation in input markdown (ANY leading space)
        const inputLinesWithIndent = markdown.split('\n').filter(line => /^\s+/.test(line));
        if (inputLinesWithIndent.length > 0) {
          console.log('🔍 [TAB_DEBUG] [AI-INPUT] Markdown from AI has indented lines:', {
            count: inputLinesWithIndent.length,
            samples: inputLinesWithIndent.slice(0, 5).map(line => {
              const leading = line.match(/^\s+/)?.[0] || '';
              const charCodes = leading.split('').map(c => c.charCodeAt(0));
              return {
                leadingCharCount: leading.length,
                leadingCharCodes: charCodes,
                isSingleTab: charCodes.length === 1 && charCodes[0] === 9,
                isFourSpaces: charCodes.length === 4 && charCodes.every(c => c === 32),
                firstCharName: line.charCodeAt(0) === 9 ? 'TAB' : 'SPACE',
                preview: JSON.stringify(line.substring(0, 50)),
                lineLength: line.length
              };
            }),
            note: 'Compare: TAB (code=9, count=1) vs SPACES (code=32, count=4)',
            source: 'RAW_AI_MARKDOWN'
          });
        }
        
        // 🧹 PHASE 1: NORMALIZE MARKDOWN INPUT
        // This is the CRITICAL FIRST STEP - clean Claude's markdown before parsing
        const { cleaned: processedMarkdown, issues: normalizationIssues, stats: normalizationStats } = MarkdownNormalizer.normalize(markdown);
        
        if (DEBUG_MARKDOWN_PIPELINE) {
          console.log('🧹 [MARKDOWN_NORMALIZER] === INPUT NORMALIZATION ===', {
            timestamp: new Date().toISOString(),
            originalLength: markdown.length,
            cleanedLength: processedMarkdown.length,
            issues: normalizationIssues,
            stats: normalizationStats,
            lengthReduction: markdown.length - processedMarkdown.length,
            phase: 'markdown_normalization'
          });
        }

        // 🔧 CLEAN ARCHITECTURE: Use shared decoder at the right boundary
        let finalMarkdown = String(processedMarkdown);
        
        // 🔍 DEBUG: Check if indentation survived normalization (check for ANY leading space)
        const linesWithIndent = finalMarkdown.split('\n').filter(line => /^\s+/.test(line));
        if (linesWithIndent.length > 0) {
          console.log('🔍 [INDENT_DEBUG] [After-Normalization] Lines with indentation detected:', {
            count: linesWithIndent.length,
            samples: linesWithIndent.slice(0, 5).map(line => ({
              leadingSpaces: line.match(/^\s+/)?.[0].length || 0,
              preview: JSON.stringify(line.substring(0, 50)),
              lineLength: line.length
            })),
            note: 'Indentation survived MarkdownNormalizer'
          });
        } else {
          console.log('⚠️ [INDENT_DEBUG] [After-Normalization] NO lines with leading spaces found after normalization:', {
            totalLines: finalMarkdown.split('\n').length,
            firstLines: finalMarkdown.split('\n').slice(0, 3).map(line => JSON.stringify(line.substring(0, 50)))
          });
        }
        
        // ✅ DECODE: Always decode entities at AI tool entry point
        try {
          const { HtmlEntityDecoder } = require('../../../../shared/utils/htmlEntityDecoder');
          finalMarkdown = HtmlEntityDecoder.decode(finalMarkdown, 'doc_write-entry');
        } catch (error) {
          console.warn('⚠️ [DOC_WRITE_ENTRY] Decoder failed, continuing with raw markdown:', error);
        }
        
        // 🔧 CUSTOM LATEX EXTRACTION: Extract LaTeX BEFORE marked.js processing
        // This avoids issues with marked-katex-extension's strict regex requirements
        // IMPORTANT: Declare outside try block so it's accessible later for restoration
        const { cleaned: markdownWithoutLatex, matches: latexMatches } = extractLatexFromMarkdown(finalMarkdown);
        finalMarkdown = markdownWithoutLatex;
        
        // Parse markdown into tokens - use the ORIGINAL working approach
        let tokens;
        try {
          // 🔍 PRE-LEXER LOGGING: Check for quotes before marked.lexer
          const hasQuotesBeforeLexer = /['"&]/g.test(finalMarkdown);
          if (hasQuotesBeforeLexer) {
            log.log('🔍 [MARKED_QUOTES_TRACE] BEFORE marked.lexer:', {
              input: finalMarkdown.substring(0, 200),
              quotesFound: finalMarkdown.match(/["']/g) || [],
              entitiesFound: finalMarkdown.match(/&[a-zA-Z0-9#]+;/g) || [],
              inputLength: finalMarkdown.length
            });
          }

          if (DEBUG_MARKDOWN_PIPELINE) {
            console.log('🔍 [MARKED_DEBUG] Attempting to parse with marked.lexer (ES6 import)...');
          }
          
          // 🔍 DEBUG: Check if markdown contains LaTeX patterns
          const hasInlineMath = /\$[^$]+\$/.test(finalMarkdown);
          const hasDisplayMath = /\$\$[^$]+\$\$/.test(finalMarkdown);
          
          // ✅ USE CANONICAL CONFIG: Ensures consistent parsing across all markdown operations
          tokens = parseMarkdownToTokens(finalMarkdown);
          
          // 🔍 POST-LEXER LOGGING: Check if quotes survived marked.lexer
          if (hasQuotesBeforeLexer) {
            const allTokenText = tokens.map((t: any) => t.text || t.raw || '').join('');
            log.log('🔍 [MARKED_QUOTES_TRACE] AFTER marked.lexer:', {
              tokenCount: tokens.length,
              allTokenText: allTokenText.substring(0, 200),
              quotesInTokens: (allTokenText.match(/["']/g) || []).length,
              entitiesInTokens: (allTokenText.match(/&[a-zA-Z0-9#]+;/g) || []).length,
              quotesPreserved: (allTokenText.match(/["']/g) || []).length > 0,
              SUCCESS: hasQuotesBeforeLexer && (allTokenText.match(/["']/g) || []).length > 0
            });
          }
          log.log('✅ [MARKED_DEBUG] SUCCESS with marked.lexer:', {
            tokenCount: tokens?.length || 0,
            tokenTypes: tokens?.map((t: any) => t.type) || []
          });
          
          // 🔍 DEBUG: Check indentation preservation in marked.lexer tokens (ANY leading space)
          const tokensWithIndent = tokens.filter((t: any) => {
            if (t.type === 'paragraph' && t.tokens) {
              return t.tokens.some((tt: any) => tt.type === 'text' && /^\s+/.test(tt.raw || tt.text || ''));
            }
            return false;
          });
          
          if (tokensWithIndent.length > 0) {
            console.log('🔍 [INDENT_DEBUG] [marked.lexer] Tokens with indentation detected:', {
              count: tokensWithIndent.length,
              samples: tokensWithIndent.slice(0, 3).map((t: any) => ({
                type: t.type,
                textHasIndent: /^\s+/.test(t.text || ''),
                rawHasIndent: /^\s+/.test(t.raw || ''),
                textPreview: JSON.stringify((t.text || '').substring(0, 40)),
                rawPreview: JSON.stringify((t.raw || '').substring(0, 40)),
                firstTextToken: t.tokens?.[0] ? {
                  type: t.tokens[0].type,
                  raw: JSON.stringify((t.tokens[0].raw || '').substring(0, 40)),
                  text: JSON.stringify((t.tokens[0].text || '').substring(0, 40))
                } : 'none'
              }))
            });
          }
          
          // 🔍 DEBUG: Log latex extraction results
        } catch (lexerError) {
          console.error('❌ [MARKED_DEBUG] marked.lexer failed:', {
            error: lexerError instanceof Error ? lexerError.message : String(lexerError),
            errorStack: lexerError instanceof Error ? lexerError.stack : null,
            errorType: typeof lexerError
          });
          
          // For now, throw the error so we can see what's really happening
          throw lexerError;
        }
        
        // 🧹 PHASE 2: FILTER EMPTY TOKENS
        // This removes space tokens and empty paragraph tokens that would create empty Y.js nodes
        const { filtered: filteredTokens, removedCount: tokenRemovalCount, removedTypes } = MarkdownNormalizer.filterEmptyTokens(tokens);
        
        log.log('🧹 [MARKDOWN_NORMALIZER] === TOKEN FILTERING ===', {
          timestamp: new Date().toISOString(),
          originalTokenCount: tokens.length,
          filteredTokenCount: filteredTokens.length,
          removedCount: tokenRemovalCount,
          removedTypes: removedTypes,
          phase: 'token_filtering'
        });
        
        // 🔴 CRITICAL: Log detailed breakdown of removed tokens
        if (tokenRemovalCount > 0) {
          const removedTokens = tokens.filter((token: any) => {
            // Match filterEmptyTokens logic
            if (token.type === 'space') return true;
            if (token.type === 'paragraph' && (!token.text || token.text.trim() === '')) return true;
            if (token.type === 'paragraph' && token.text && /^\s+$/.test(token.text)) return true;
            return false;
          });
          
          log.log('🔴 [REMOVED_TOKENS_DETAIL] Breakdown of removed tokens', {
            totalRemoved: removedTokens.length,
            spaceTokens: removedTokens.filter((t: any) => t.type === 'space').length,
            emptyParagraphs: removedTokens.filter((t: any) => t.type === 'paragraph' && (!t.text || t.text.trim() === '')).length,
            whitespaceOnlyParagraphs: removedTokens.filter((t: any) => t.type === 'paragraph' && t.text && /^\s+$/.test(t.text)).length,
            removedTokenTypes: removedTokens.map((t: any) => t.type),
            preservedTokenTypes: filteredTokens.map((t: any) => t.type)
          });
        }

        // Markdown parsed into tokens (verbose logging removed)
        
        // Convert tokens to content array format for Insert command
        const contentArray: any[] = [];

        // 🔍 QUOTE CONVERSION TRACE: Check tokens before conversion
        const hasQuotesInTokens = filteredTokens.some((t: any) => 
          (t.text && /["']/.test(t.text)) || 
          (t.raw && /["']/.test(t.raw)) ||
          (t.tokens && (JSON.stringify(t.tokens).includes('"') || JSON.stringify(t.tokens).includes("'")))
        );
        
        if (hasQuotesInTokens) {
          log.log('🔍 [TOKEN_CONVERSION_TRACE] Tokens BEFORE contentArray conversion:', {
            tokenCount: filteredTokens.length,
            tokenTypes: filteredTokens.map((t: any) => t.type),
            sampleTokenText: filteredTokens.map((t: any) => t.text || t.raw || '').join('').substring(0, 150),
            quotesInRawTokens: (filteredTokens.map((t: any) => t.text || t.raw || '').join('').match(/["']/g) || []).length,
            SOURCE: 'TOKEN_CONVERSION_BEFORE'
          });
        }


        // Inline token flattener → { text, formatting[] }
        /**
         * 🎨 HELPER: Parse CSS style string into ProseMirror mark objects
         * Converts inline CSS to our mark format for Y.js
         * Example: "color: red; font-size: 16pt" → {textColor: {color: 'red'}, fontSize: {size: 16}}
         */
        const parseStyleString = (styleStr: string): Record<string, any> => {
          const marks: Record<string, any> = {};
          const unsupported: string[] = [];
          
          // Split by semicolon and process each property
          const props = styleStr.split(';').map(p => p.trim()).filter(Boolean);
          
          for (const prop of props) {
            const colonIndex = prop.indexOf(':');
            if (colonIndex === -1) continue;
            
            const key = prop.substring(0, colonIndex).trim().toLowerCase();
            const value = prop.substring(colonIndex + 1).trim();
            
            if (!value) continue;
            
            switch (key) {
              case 'color':
                // Validate color format (basic check for common formats)
                if (/^(#[0-9a-f]{3,8}|[a-z]+|rgb|rgba|hsl|hsla)/i.test(value)) {
                  marks.textColor = { color: value };
                } else {
                  console.warn('[HTML_STYLE] Invalid color value:', value);
                }
                break;
                
              case 'background-color':
              case 'background':
                if (/^(#[0-9a-f]{3,8}|[a-z]+|rgb|rgba|hsl|hsla)/i.test(value)) {
                  marks.backgroundColor = { backgroundColor: value };
                } else {
                  console.warn('[HTML_STYLE] Invalid background-color value:', value);
                }
                break;
                
              case 'font-family':
                // Remove quotes and validate
                const cleanFont = value.replace(/^['"]|['"]$/g, '').trim();
                if (cleanFont.length > 0) {
                  marks.fontFamily = { font: cleanFont };
                }
                break;
                
              case 'font-size':
                const sizeMatch = value.match(/^(\d+(?:\.\d+)?)(pt|px|em|rem)?$/i);
                if (sizeMatch) {
                  let size = parseFloat(sizeMatch[1]);
                  const unit = (sizeMatch[2] || 'pt').toLowerCase();
                  
                  // Convert all units to points
                  if (unit === 'px') {
                    size = Math.round(size * 0.75);  // 96 DPI: 1pt = 4/3px
                  } else if (unit === 'em' || unit === 'rem') {
                    size = Math.round(size * 12);  // Assume 12pt base font
                  }
                  
                  // Clamp to reasonable range (6pt to 72pt)
                  size = Math.max(6, Math.min(72, size));
                  marks.fontSize = { size };
                } else {
                  console.warn('[HTML_STYLE] Invalid font-size value:', value);
                }
                break;
                
              case 'font-weight':
                const weight = value.toLowerCase();
                const numericWeight = parseInt(value);
                
                // Map to semantic strong mark if bold
                if (weight === 'bold' || weight === 'bolder' || numericWeight >= 600) {
                  marks.strong = true;
                }
                
                // Also store full weight value in fontWeight mark (for non-bold weights like 300, 500)
                if (!isNaN(numericWeight) && numericWeight !== 400) {
                  marks.fontWeight = { weight: String(numericWeight) };
                } else if (weight && weight !== 'normal' && weight !== 'bold') {
                  marks.fontWeight = { weight: value };
                }
                break;
                
              case 'font-style':
                if (value.toLowerCase() === 'italic') {
                  marks.em = true;
                }
                break;
                
              case 'text-decoration':
              case 'text-decoration-line':
                const decorValue = value.toLowerCase();
                if (decorValue.includes('underline')) {
                  marks.underline = true;
                }
                if (decorValue.includes('line-through')) {
                  marks.strike = true;
                }
                break;
                
              case 'vertical-align':
                const alignValue = value.toLowerCase();
                if (alignValue === 'super' || alignValue === 'superscript') {
                  marks.superscript = true;
                } else if (alignValue === 'sub' || alignValue === 'subscript') {
                  marks.subscript = true;
                }
                break;
                
              default:
                unsupported.push(key);
                break;
            }
          }
          
          // Log unsupported properties for debugging
          if (unsupported.length > 0) {
            console.warn('⚠️ [HTML_STYLE] Unsupported CSS properties (ignored):', {
              properties: unsupported,
              supported: 'color, background-color, font-family, font-size, font-weight, font-style, text-decoration, vertical-align',
              note: 'Only inline text styling is supported. Layout properties (margin, padding, etc.) are ignored.'
            });
          }
          
          return marks;
        };
        
        /**
         * 🎨 HELPER: Detect if HTML token is a styled span tag
         * Returns info about opening/closing tags and extracted styles
         */
        const detectStyledSpan = (htmlStr: string): {isOpen: boolean; isClose: boolean; styles: Record<string, any>} => {
          // Check for opening <span style="...">
          const openMatch = htmlStr.match(/<span\s+style="([^"]+)"[^>]*>/i);
          if (openMatch) {
            return {
              isOpen: true,
              isClose: false,
              styles: parseStyleString(openMatch[1])
            };
          }
          
          // Check for closing </span>
          if (htmlStr.match(/<\/span>/i)) {
            return {
              isOpen: false,
              isClose: true,
              styles: {}
            };
          }
          
          // Not a styled span (might be other HTML)
          return {isOpen: false, isClose: false, styles: {}};
        };
        
        const flattenInline = (inlineTokens: any[] | undefined): { text: string; formatting: Array<{ start: number; end: number; marks: Record<string, any> }> } => {

          const parts: { text: string; marks: Record<string, any> }[] = [];
          
          // 🎨 NEW: Stack for tracking nested <span style="..."> tags
          const spanStyleStack: Record<string, any>[] = [];
          
          // 🎨 NEW: Get combined styles from all active spans in the stack
          const getCurrentSpanStyles = (): Record<string, any> => {
            // Merge all styles from stack (outer to inner, later overrides earlier)
            return spanStyleStack.reduce((acc, styles) => ({...acc, ...styles}), {});
          };
          
          const walk = (tks: any[], active: Record<string, any> = {}) => {
            for (const t of tks || []) {
              // 🎨 CRITICAL: Merge active marks (from markdown) with span styles (from HTML)
              const spanStyles = getCurrentSpanStyles();
              const combinedMarks = { ...active, ...spanStyles };
              
              // Marks combined for text token
              
              // 🔍 TOKEN PROPERTIES TRACE: Check actual token properties
              if (t && (t.text || t.raw) && (/["']/.test(String(t.text || '')) || /["']/.test(String(t.raw || '')))) {
                log.log('🔍 [TOKEN_PROPERTIES_TRACE] Individual token analysis:', {
                  tokenType: t.type,
                  tokenText: t.text ? String(t.text).substring(0, 100) : null,
                  tokenRaw: t.raw ? String(t.raw).substring(0, 100) : null,
                  quotesInText: t.text ? (String(t.text).match(/["']/g) || []).length : 0,
                  quotesInRaw: t.raw ? (String(t.raw).match(/["']/g) || []).length : 0,
                  entitiesInText: t.text ? (String(t.text).match(/&[a-zA-Z0-9#]+;/g) || []).length : 0,
                  entitiesInRaw: t.raw ? (String(t.raw).match(/&[a-zA-Z0-9#]+;/g) || []).length : 0,
                  SOURCE: 'TOKEN_PROPERTIES_INDIVIDUAL'
                });
              }
              switch (t.type) {
                case 'text':
                  if (t.tokens && Array.isArray(t.tokens)) {
                    walk(t.tokens, active);
                  } else if (t.raw || t.text) {
                    // 🔧 FIX: Use t.raw (original) instead of t.text (HTML-encoded)
                    const sourceText = t.raw || t.text;  // Prefer raw over text
                    const convertedText = String(sourceText);
                    parts.push({ text: convertedText, marks: combinedMarks });  // ← Use combined marks
                  }
                  break;
                case 'strong':
                  walk(t.tokens || [{ type: 'text', text: t.raw || t.text }], { ...active, strong: true });
                  break;
                case 'em':
                case 'emphasis':
                  walk(t.tokens || [{ type: 'text', text: t.raw || t.text }], { ...active, em: true });
                  break;
                case 'codespan':
                  // ✅ CRITICAL FIX: Use t.text (without backticks) not t.raw (with backticks)
                  // marked.js codespan: { raw: '`code`', text: 'code' }
                  // We want: 'code' (without backticks), not '`code`' (with backticks)
                  // ✅ ALSO FIX: Decode HTML entities from marked.js
                  let codespanText = String(t.text || '');
                  try {
                    const { HtmlEntityDecoder } = require('../../../../shared/utils/htmlEntityDecoder');
                    codespanText = HtmlEntityDecoder.decode(codespanText, 'inline_code');
                  } catch (error) {
                    // Decoder unavailable, use raw text
                  }
                  walk([{ type: 'text', text: codespanText }], { ...active, code: true });
                  break;
                case 'link':
                  // ✅ FIX: Use object structure that matches ProseMirror schema
                  // ProseMirror expects: { href: string, title?: string }
                  walk(t.tokens || [{ type: 'text', text: t.raw || t.text }], { 
                    ...active, 
                    link: { href: t.href, title: t.title || null }
                  });
                  break;
                case 'del':
                case 'strike':
                  // Support for ~~strikethrough~~ text
                  walk(t.tokens || [{ type: 'text', text: t.raw || t.text }], { ...active, strike: true });
                  break;
                  
                case 'html':
                  // 🎨 NEW: Handle inline HTML for visual styling
                  const htmlRaw = t.raw || t.text || '';
                  
                  // 🔧 CRITICAL FIX: Extract leading whitespace from HTML token
                  // When indented HTML like "\t<span>" is tokenized, the tab is in the HTML token's raw field
                  // We need to extract it and add as text BEFORE processing the HTML tag
                  const leadingWhitespace = htmlRaw.match(/^(\s+)/)?.[1] || '';
                  if (leadingWhitespace && parts.length === 0) {
                    // This is the first token AND it has leading whitespace (indentation)
                    // Add the whitespace as a text part so it's preserved
                    parts.push({ text: leadingWhitespace, marks: combinedMarks });
                  }
                  
                  const spanInfo = detectStyledSpan(htmlRaw);
                  
                  if (spanInfo.isOpen) {
                    // Opening styled span - push styles to stack
                    spanStyleStack.push(spanInfo.styles);
                    console.log('🎨 [HTML_STYLE] Opening styled span:', {
                      styles: Object.keys(spanInfo.styles),
                      styleValues: spanInfo.styles,
                      stackDepth: spanStyleStack.length,
                      rawHtml: htmlRaw.substring(0, 100)
                    });
                    // Styles extracted from HTML span
                  } else if (spanInfo.isClose) {
                    // Closing span - pop from stack
                    if (spanStyleStack.length > 0) {
                      const popped = spanStyleStack.pop();
                      console.log('🎨 [HTML_STYLE] Closing styled span:', {
                        poppedStyles: Object.keys(popped!),
                        remainingDepth: spanStyleStack.length
                      });
                    } else {
                      console.warn('⚠️ [HTML_STYLE] Closing </span> without matching opening tag:', {
                        htmlRaw: htmlRaw
                      });
                    }
                  } else {
                    // Other HTML (not styled span) - strip tags, preserve text
                    const stripped = htmlRaw.replace(/<[^>]*>/g, '');
                    if (stripped) {
                      console.log('🔍 [HTML_STYLE] Non-styled HTML, extracting text:', {
                        original: htmlRaw.substring(0, 50),
                        stripped: stripped.substring(0, 50)
                      });
                      parts.push({ text: stripped, marks: combinedMarks });
                    }
                  }
                  break;
                  
                default:
                  if (t.raw || t.text) {
                    // 🔧 FIX: Use t.raw (original) instead of t.text (HTML-encoded) in default case
                    const sourceText = t.raw || t.text;  // Prefer raw over text
                    const convertedText = String(sourceText);
                    parts.push({ text: convertedText, marks: combinedMarks });  // ← Use combined marks
                  }
                  break;
              }
            }
          };
          walk(inlineTokens || []);
          
          // ✅ VALIDATION: Check for unclosed styled spans
          if (spanStyleStack.length > 0) {
            console.warn('⚠️ [HTML_STYLE] Unclosed styled span tags detected:', {
              unclosedCount: spanStyleStack.length,
              remainingStyles: spanStyleStack.map(s => Object.keys(s)),
              note: 'Styles were applied to available text before end of paragraph'
            });
          }
          
          // ✅ FIX: Preserve leading whitespace (indentation) but trim trailing
          // Leading spaces may be intentional for formatting, trailing are likely accidental
          const beforeTrim = parts.map(p => p.text).join('');
          const text = beforeTrim.replace(/\s+$/, '');
          
          // 🔍 DEBUG: Log indentation preservation in flattenInline
          const hasLeading = /^\s+/.test(beforeTrim);
          if (hasLeading) {
            const leadingCount = beforeTrim.match(/^\s+/)?.[0].length || 0;
            const preservedCount = text.match(/^\s+/)?.[0].length || 0;
            console.log('🔍 [INDENT_DEBUG] [flattenInline] Whitespace handling:', {
              beforeTrim: JSON.stringify(beforeTrim.substring(0, 50)),
              afterTrim: JSON.stringify(text.substring(0, 50)),
              leadingSpacesBefore: leadingCount,
              leadingSpacesAfter: preservedCount,
              preserved: leadingCount === preservedCount,
              operation: 'replace(/\\s+$/, "")'
            });
          }
          
          // Clamp end to trimmed text length. Ranges are computed from original part lengths,
          // but text has trailing whitespace removed. Without clamping, a formatted part that
          // includes trailing whitespace would produce an out-of-bounds end index which
          // createElementWithFormatting rejects entirely, silently dropping the formatting.
          const formatting: Array<{ start: number; end: number; marks: Record<string, any> }> = [];
          let offset = 0;
          for (const p of parts) {
            const len = p.text.length;
            if (len > 0 && p.marks && Object.keys(p.marks).length > 0) {
              const start = offset;
              const end = Math.min(offset + len, text.length);
              if (start < text.length) {
                formatting.push({ start, end, marks: p.marks });
              }
            }
            offset += len;
          }
          
          return { text, formatting };
        };

        const makeParagraphFrom = (token: any): TextContentItem => {
          const result = flattenInline(token.tokens || []);
          const text = result?.text || '';
          const formatting = result?.formatting || [];
          // 🔧 FIX: Use token.raw (original) instead of token.text (HTML-encoded) as fallback
          // ✅ FIX: Preserve leading spaces (indentation) but trim trailing
          const rawText = text || (token.raw || token.text || '');
          const finalText = rawText.replace(/\s+$/, '');  // Only trim trailing
          
          // 🔍 DEBUG: Log indentation preservation in makeParagraphFrom
          const hasLeading = /^\s+/.test(rawText);
          if (hasLeading) {
            const leadingBefore = rawText.match(/^\s+/)?.[0].length || 0;
            const leadingAfter = finalText.match(/^\s+/)?.[0].length || 0;
            console.log('🔍 [INDENT_DEBUG] [makeParagraphFrom] Paragraph text processing:', {
              rawText: JSON.stringify(rawText.substring(0, 50)),
              finalText: JSON.stringify(finalText.substring(0, 50)),
              leadingSpacesBefore: leadingBefore,
              leadingSpacesAfter: leadingAfter,
              preserved: leadingBefore === leadingAfter,
              tokenRaw: token.raw ? JSON.stringify(token.raw.substring(0, 30)) : 'none',
              tokenText: token.text ? JSON.stringify(token.text.substring(0, 30)) : 'none'
            });
          }
          
          return { type: NODE_TYPES.PARAGRAPH, text: finalText, formatting };
        };
        
        // LaTeX validation helper - validates before creating math nodes
        const validateLatex = (latex: string): { valid: boolean; error?: string } => {
          // 🔧 DEFENSIVE: Ensure latex is a string
          if (typeof latex !== 'string') {
            console.warn('[LaTeX Validation] Non-string LaTeX input:', { type: typeof latex });
            return { valid: false, error: 'LaTeX must be a string' };
          }
          
          if (!latex || latex.trim().length === 0) {
            return { valid: false, error: 'Empty LaTeX string' };
          }
          
          // Security: Prevent extremely large LaTeX strings (DOS protection)
          if (latex.length > 10000) {
            console.warn('[LaTeX Validation] LaTeX too long:', { length: latex.length });
            return { valid: false, error: 'LaTeX string too long (>10000 chars)' };
          }
          
          // 🔧 SECURITY: Check for suspicious patterns (basic XSS prevention)
          if (latex.includes('<script') || latex.includes('javascript:')) {
            console.error('[LaTeX Validation] Suspicious content detected:', {
              latex: latex.substring(0, 100)
            });
            return { valid: false, error: 'Suspicious content detected' };
          }
          
          try {
            // Validate with KaTeX (doesn't render, just validates syntax)
            katex.renderToString(latex, {
              displayMode: false,
              throwOnError: true,
              trust: false,
              maxSize: 500,
              maxExpand: 1000,
              strict: 'ignore'
            });
            return { valid: true };
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.warn('[LaTeX Validation] Invalid LaTeX detected:', {
              latex: latex.substring(0, 100),
              error: errorMsg
            });
            return { valid: false, error: errorMsg };
          }
        };
        
        /**
         * 🆕 RECURSIVE HELPER: Process list items with arbitrary nesting depth
         * This extracts paragraph text AND nested lists recursively
         */
        const processListItemRecursive = (item: any, depth: number = 0): any => {
          // Extract paragraph text (filter out nested lists to avoid concatenation)
          const nonListTokens = (item.tokens || []).filter((t: any) => t.type !== 'list');
          const result = flattenInline(nonListTokens);
          const text = result?.text || '';
          const formatting = result?.formatting || [];
          
          // Handle checkboxes/task lists
          let checked: boolean | null = null;
          if (item.task === true) checked = !!item.checked;
          
          // ✅ FIX: Preserve leading spaces (indentation) in list items
          const rawListText = text || (item.raw || item.text || '');
          let paraText = rawListText.replace(/\s+$/, '');  // Only trim trailing
          
          // 🔍 DEBUG: Log list item indentation
          const hasLeadingInList = /^\s+/.test(rawListText);
          if (hasLeadingInList) {
            const leadingBefore = rawListText.match(/^\s+/)?.[0].length || 0;
            const leadingAfter = paraText.match(/^\s+/)?.[0].length || 0;
            console.log('🔍 [INDENT_DEBUG] [ListItem] Processing list item text:', {
              rawText: JSON.stringify(rawListText.substring(0, 40)),
              paraText: JSON.stringify(paraText.substring(0, 40)),
              leadingSpacesBefore: leadingBefore,
              leadingSpacesAfter: leadingAfter,
              preserved: leadingBefore === leadingAfter,
              source: text ? 'flattenInline' : (item.raw ? 'item.raw' : 'item.text')
            });
          }
          
          const checkboxMatch = paraText.match(/^\s*\[( |x|X)\]\s+(.*)$/);
          if (checked === null && checkboxMatch) {
            checked = checkboxMatch[1].toLowerCase() === 'x';
            // After extracting checkbox, preserve any leading spaces in the text
            paraText = checkboxMatch[2];  // Text after checkbox (checkbox regex already handled leading \s*)
          }
          
          const liParagraph = { 
            type: NODE_TYPES.PARAGRAPH, 
            text: paraText, 
            formatting 
          };
          
          // 🔁 RECURSIVELY extract nested lists
          const nestedBlocks: any[] = [];
          for (const t of (item.tokens || [])) {
            if (t.type === 'list') {
              const nestedListType = t.ordered ? 'ordered_list' : 'bullet_list';
              // 🔁 RECURSIVE CALL: Process each nested item with incremented depth
              const nestedItems = (t.items || []).map((ni: any) => processListItemRecursive(ni, depth + 1));
              nestedBlocks.push({ type: nestedListType, content: nestedItems });
              
              console.log(`${'  '.repeat(depth)}🔁 [RECURSIVE_PARSE] Depth ${depth}: Found nested ${nestedListType} with ${nestedItems.length} items`);
            }
          }
          
          const li: any = { 
            type: NODE_TYPES.LIST_ITEM, 
            content: [liParagraph, ...nestedBlocks] 
          };
          if (checked !== null) li.attrs = { checked };
          
          // 🔍 DEBUG: Log when nested blocks are found
          if (nestedBlocks.length > 0 && depth === 0) {
            console.log('🔍 [LIST_NESTING_DEBUG] Created list item with nested blocks:', {
              parentText: paraText.substring(0, 30),
              nestedBlockCount: nestedBlocks.length,
              nestedBlockTypes: nestedBlocks.map(nb => nb.type),
              totalContentItems: li.content.length,
              contentStructure: li.content.map((c: any) => ({ type: c.type, hasContent: !!c.content }))
            });
          }
          
          return li;
        };
        
        for (const token of filteredTokens) {
          switch (token.type) {
            case TOKEN_TYPES.HEADING:
              const level = token.depth || token.level || 1;
              {
                const result = flattenInline(token.tokens || []);
                const text = result?.text || '';
                const formatting = result?.formatting || [];
                
                // ✅ FIX: Decode fallback text to handle rare malformed headings
                // If flattenInline returns empty (malformed markdown), we fall back to token.text
                // which is HTML-encoded by marked.js and needs decoding
                let fallbackText = token.text || '';
                if (!text && fallbackText) {
                  try {
                    const { HtmlEntityDecoder } = require('../../../../shared/utils/htmlEntityDecoder');
                    const beforeDecode = fallbackText;
                    fallbackText = HtmlEntityDecoder.decode(fallbackText, 'heading_fallback');
                    if (fallbackText !== beforeDecode) {
                      console.log('🔧 [HEADING_FALLBACK_DECODE] Decoded malformed heading:', {
                        before: beforeDecode.substring(0, 100),
                        after: fallbackText.substring(0, 100),
                        reason: 'token.tokens_was_empty_or_invalid'
                      });
                    }
                  } catch (error) {
                    // Keep original if decoder fails
                  }
                }
                
                // ✅ NOTE: Headings are ALWAYS trimmed (no indentation for headings)
                // This is intentional - headings should never have leading spaces
                let headingText = (text || fallbackText).trim();
                
                // ✅ DEFENSIVE: Remove ALL newlines from heading text (literal \n, \r\n, \r)
                // Newlines in headings indicate parser failure - strip them entirely and collapse whitespace
                headingText = headingText.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
                
                // ✅ VALIDATION: Warn if heading is suspiciously long (>200 chars)
                // Long headings suggest markdown parser merged heading with subsequent content
                if (headingText.length > 200) {
                  console.warn('⚠️ [HEADING_VALIDATION] Suspiciously long heading detected:', {
                    length: headingText.length,
                    preview: headingText.substring(0, 100) + '...',
                    level: level,
                    possibleCause: 'Malformed markdown merged heading with content'
                  });
                  // Truncate at 200 chars to prevent entire document becoming heading
                  headingText = headingText.substring(0, 200) + '...';
                }
                
                contentArray.push({
                  type: NODE_TYPES.HEADING,
                  text: headingText,
                  formatting,
                  attrs: { level: Math.max(1, Math.min(6, level)) }
                });
              }
              break;
              
            case TOKEN_TYPES.PARAGRAPH: {
              // marked.js v4 never produces a top-level image token — images are always inline
              // tokens inside a paragraph. Detect them here and emit as image nodes.
              const inlineTokens: any[] = token.tokens || [];
              const hasImages = inlineTokens.some((t: any) => t.type === 'image');

              if (!hasImages) {
                contentArray.push(makeParagraphFrom(token));
              } else {
                let textBuffer: any[] = [];
                for (const inlineTok of inlineTokens) {
                  if (inlineTok.type === 'image') {
                    if (textBuffer.length > 0) {
                      const result = flattenInline(textBuffer);
                      if (result.text.trim()) {
                        contentArray.push({ type: NODE_TYPES.PARAGRAPH, text: result.text, formatting: result.formatting });
                      }
                      textBuffer = [];
                    }
                    contentArray.push({
                      type: 'image',
                      attrs: {
                        src: inlineTok.href || '',
                        alt: inlineTok.text || '',
                        title: inlineTok.title || null
                      }
                    });
                  } else {
                    textBuffer.push(inlineTok);
                  }
                }
                if (textBuffer.length > 0) {
                  const result = flattenInline(textBuffer);
                  if (result.text.trim()) {
                    contentArray.push({ type: NODE_TYPES.PARAGRAPH, text: result.text, formatting: result.formatting });
                  }
                }
              }
              break;
            }
            
            // Note: LaTeX is handled via post-processing (see below)
              
            case TOKEN_TYPES.LIST:
              // Lists require nested structure - use recursive helper for arbitrary depth
              const listType = token.ordered ? NODE_TYPES.ORDERED_LIST : NODE_TYPES.BULLET_LIST;
              const listItems = (token.items || []).map((item: any) => processListItemRecursive(item));
              
              contentArray.push({
                type: listType,
                content: listItems
              });
              break;
              
            case 'blockquote':
              // Blockquotes can contain nested content - preserve structure
              const blockquoteContent = token.tokens && token.tokens.length > 0
                ? token.tokens.map((t: any) => {
                    // 🚨 FIX: Respect actual token types in blockquotes (headings, lists, etc.)
                    if (t.type === 'heading') {
                      const result = flattenInline(t.tokens || []);
                      const text = result?.text || '';
                      const formatting = result?.formatting || [];
                      return { type: NODE_TYPES.HEADING, text: (text || (t.text || '')).trim(), formatting, attrs: { level: t.depth || t.level || 1 } };
                    } else if (t.type === 'list') {
                      // Nested list in blockquote - use recursive helper for arbitrary depth
                      return {
                        type: t.ordered ? 'ordered_list' : 'bullet_list',
                        content: (t.items || []).map((item: any) => processListItemRecursive(item))
                      };
                    } else if (t.type === 'paragraph') {
                      return makeParagraphFrom(t);
                    } else {
                      // Default to paragraph with inline handling
                      return makeParagraphFrom(t);
                    }
                  })
                : [makeParagraphFrom(token)];
              
              contentArray.push({
                type: 'blockquote',
                content: blockquoteContent
              });
              break;
            case 'table':
              // GFM Table support
              {
                // 🐛 FIX: Extract text from cell objects instead of stringifying them
                // marked.js returns cell objects (with tokens, text, raw properties), not strings
                // String(object) would produce "[object Object]" causing rendering errors
                const extractCellText = (cell: any): { text: string; formatting: any[] } => {
                  // If cell is already a string, use it
                  if (typeof cell === 'string') {
                    return { text: cell, formatting: [] };
                  }
                  
                  // If cell is an object with tokens (marked.js format), flatten it
                  if (cell && typeof cell === 'object') {
                    if (cell.tokens && Array.isArray(cell.tokens)) {
                      // Use flattenInline to properly extract formatted text (handles bold, italic, links, etc.)
                      const result = flattenInline(cell.tokens);
                      return { text: result.text || '', formatting: result.formatting || [] };
                    }
                    
                    // Fallback to text or raw properties
                    if (cell.text) {
                      return { text: String(cell.text), formatting: [] };
                    }
                    
                    if (cell.raw) {
                      return { text: String(cell.raw), formatting: [] };
                    }
                  }
                  
                  // Last resort: empty string (prevents "[object Object]" errors)
                  console.warn('[TABLE] Could not extract text from cell, using empty string');
                  return { text: '', formatting: [] };
                };
                
                const headerCells = (token.header || []).map((h: any) => {
                  const extracted = extractCellText(h);
                  return {
                    type: NODE_TYPES.TABLE_HEADER,
                    content: [{ type: NODE_TYPES.PARAGRAPH, text: extracted.text, formatting: extracted.formatting }]
                  };
                });
                
                const headerRow = headerCells.length > 0 ? [{ type: 'table_row', content: headerCells }] : [];
                
                const bodyRows = (token.rows || []).map((row: any[]) => ({
                  type: 'table_row',
                  content: row.map((cell: any) => {
                    const extracted = extractCellText(cell);
                    return { 
                      type: NODE_TYPES.TABLE_CELL, 
                      content: [{ type: NODE_TYPES.PARAGRAPH, text: extracted.text, formatting: extracted.formatting }] 
                    };
                  })
                }));
                
                contentArray.push({ type: 'table', content: [...headerRow, ...bodyRows] });
              }
              break;
              
              contentArray.push({
                type: 'blockquote',
                content: blockquoteContent
              });
              break;
              
            case 'code':
              // ✅ FIX: Decode HTML entities that marked.js encodes in the text property
              // marked.js encodes quotes/apostrophes as &#39; even with sanitize: false
              // We MUST decode after marked.lexer to restore original characters
              let finalCodeText = token.text || '';
              try {
                const { HtmlEntityDecoder } = require('../../../../shared/utils/htmlEntityDecoder');
                const rawCodeText = token.text || '';
                const decodedCodeText = HtmlEntityDecoder.decode(rawCodeText, 'code_block');
                
                if (decodedCodeText !== rawCodeText) {
                  console.log('🔧 [CODE_BLOCK_DECODE] Decoded HTML entities in code block:', {
                    before: rawCodeText.substring(0, 100),
                    after: decodedCodeText.substring(0, 100),
                    entitiesFound: rawCodeText.match(/&[a-zA-Z0-9#]+;/g) || []
                  });
                }
                finalCodeText = decodedCodeText;
              } catch (error) {
                console.warn('⚠️ [CODE_BLOCK_DECODE] Decoder failed, using raw text:', error);
              }
              
              contentArray.push({
                type: 'code_block',
                text: finalCodeText,  // ← Use decoded text
                attrs: { language: token.lang || '' }
              });
              break;
              
            case 'hr':
            case 'thematicBreak':
              contentArray.push({
                type: 'horizontal_rule'
              });
              break;
              
            default:
              // Unknown token type - create paragraph as fallback (single-line warn)
              console.warn('⚠️ [INSERT_MARKDOWN] Unknown token, creating paragraph:', token.type);
              if (token.tokens && token.tokens.length > 0) {
                contentArray.push(makeParagraphFrom(token));
              } else if (token.text || token.raw) {
                // ✅ FIX: Preserve leading spaces even in fallback case
                const t = { type: NODE_TYPES.PARAGRAPH, text: (token.text || token.raw || '').replace(/\s+$/, '') };
                contentArray.push(t);
              }
              break;
          }
        }
        

        // 🔧 PHASE 2.5: RESTORE LATEX PLACEHOLDERS
        // Scan contentArray for LaTeX placeholders and replace them with math nodes
        
        const contentArrayWithMath: any[] = [];
        
        for (let i = 0; i < contentArray.length; i++) {
          const item = contentArray[i];
          
          if (item.type === 'paragraph' && item.text) {
            const text = item.text;
            
            // Quick check: does this text contain any placeholder?
            if (!text.includes('⟦LATEX_')) {
              contentArrayWithMath.push(item);
              continue;
            }
            
            // Find which placeholder (if any) is in this text
            const foundMatch = latexMatches.find(m => text.includes(m.placeholder));
            
            if (foundMatch) {
              
              // Check if the ENTIRE paragraph is just a single placeholder
              if (text.trim() === foundMatch.placeholder) {
                // This paragraph is ONLY a placeholder - replace with math node
                const validation = validateLatex(foundMatch.latex);
                if (validation.valid) {
                  contentArrayWithMath.push({
                    type: foundMatch.displayMode ? 'math_display' : 'math_inline',
                    latex: foundMatch.latex,
                    attrs: {}
                  });
                } else {
                  // 🔧 FIX: Invalid LaTeX - don't keep placeholder, skip entirely
                  console.warn('⚠️ [LATEX_RESTORE] Skipping invalid LaTeX paragraph:', {
                    placeholder: foundMatch.placeholder,
                    error: validation.error
                  });
                  // Don't push anything - effectively removes the placeholder
                }
              } else {
                // Paragraph contains mixed text and placeholders - split it
                // 🔧 FIX: Process placeholders in the order they appear in TEXT, not array order!
                let remainingText = text;
                const segments: any[] = [];
                
                // Keep processing until no more placeholders in remainingText
                while (remainingText.length > 0) {
                  // Find the FIRST placeholder in remainingText (any placeholder, in text order)
                  let earliestMatch: { match: any; idx: number } | null = null;
                  
                  for (const match of latexMatches) {
                    const idx = remainingText.indexOf(match.placeholder);
                    if (idx !== -1) {
                      // Found a placeholder - is it earlier than what we've seen?
                      if (!earliestMatch || idx < earliestMatch.idx) {
                        earliestMatch = { match, idx };
                      }
                    }
                  }
                  
                  // No more placeholders found
                  if (!earliestMatch) {
                    break;
                  }
                  
                  const { match, idx } = earliestMatch;
                  
                  // Add text before placeholder (if any)
                  if (idx > 0) {
                    // ✅ FIX: Preserve leading spaces in text before LaTeX
                    const beforeText = remainingText.substring(0, idx).replace(/\s+$/, '');
                    if (beforeText) {
                      segments.push({ type: 'text', content: beforeText });
                    }
                  }
                  
                  // Add math node (if valid), otherwise skip the placeholder entirely
                  if (validateLatex(match.latex).valid) {
                    segments.push({
                      type: 'math',
                      mathType: match.displayMode ? 'math_display' : 'math_inline',
                      latex: match.latex
                    });
                  } else {
                    // 🔧 FIX: Invalid LaTeX - just skip, don't include placeholder
                    console.warn('⚠️ [LATEX_RESTORE] Skipping invalid LaTeX in mixed text:', {
                      placeholder: match.placeholder
                    });
                  }
                  
                  // Move past this placeholder
                  remainingText = remainingText.substring(idx + match.placeholder.length);
                }
                
                // Add remaining text (if any)
                // ✅ FIX: Preserve leading spaces in text after LaTeX
                const finalText = remainingText.replace(/\s+$/, '');
                if (finalText) {
                  segments.push({ type: 'text', content: finalText });
                }
                
                // Convert segments to content items
                for (const segment of segments) {
                  if (segment.type === 'math') {
                    contentArrayWithMath.push({
                      type: segment.mathType,
                      latex: segment.latex,
                      attrs: {}
                    });
                  } else if (segment.type === 'text') {
                    contentArrayWithMath.push({
                      type: 'paragraph',
                      text: segment.content,
                      formatting: item.formatting || []
                    });
                  }
                }
              }
            } else {
              // No placeholders, keep as is
              contentArrayWithMath.push(item);
            }
          } else {
            // Not a paragraph or no text, keep as is
            contentArrayWithMath.push(item);
          }
        }
        
        // 🔧 LAYER 3: Final safety net - remove any remaining placeholders that slipped through
        const cleanedContentArray = contentArrayWithMath.map((item: any) => {
          if (item.type === 'paragraph' && item.text && item.text.includes('⟦LATEX_')) {
            // Found orphaned placeholder - remove it
            const cleanedText = item.text.replace(/⟦LATEX_(DISPLAY|INLINE)_\d+⟧/g, '');
            
            // If text becomes empty after removing placeholder, skip this item
            if (!cleanedText.trim()) {
              console.warn('⚠️ [LATEX_CLEANUP] Removed paragraph with only placeholder');
              return null;
            }
            
            console.warn('⚠️ [LATEX_CLEANUP] Removed orphaned placeholder from text:', {
              before: item.text.substring(0, 100),
              after: cleanedText.substring(0, 100)
            });
            
            // ✅ FIX: Preserve leading spaces when cleaning LaTeX placeholders
            return { ...item, text: cleanedText.replace(/\s+$/, '') };
          }
          return item;
        }).filter((item: any) => item !== null);  // Remove null items
        
        // 🔧 FIX: Always use cleanedContentArray (never fall back to contentArray which has placeholders!)
        // If it's empty, that's better than showing placeholders
        const finalContentArray = cleanedContentArray;
        
        // 🧹 PHASE 3: VALIDATE CONTENT ARRAY
        // Remove any empty paragraph items that slipped through token filtering
        const { validated: validatedContentArray, removedCount: contentRemovalCount } = MarkdownNormalizer.validateContentArray(finalContentArray);
        
        // 🔍 LATEX DEBUG: Check if any math items were created
        const mathItems = validatedContentArray.filter((item: any) => 
          item.type === 'math_inline' || item.type === 'math_display'
        );

        if (contentRemovalCount > 0) {
          log.log('🧹 [MARKDOWN_NORMALIZER] === CONTENT ARRAY VALIDATION ===', {
            timestamp: new Date().toISOString(),
            originalCount: contentArray.length,
            validatedCount: validatedContentArray.length,
            removedCount: contentRemovalCount,
            phase: 'content_validation'
          });
        }

        // 🔴 CRITICAL: Log final content array breakdown to confirm no space tokens leaked through
        log.log('🔴 [CONTENT_ARRAY_BREAKDOWN] Final content items before return', {
          totalItems: validatedContentArray.length,
          itemTypes: validatedContentArray.map((item: any) => item?.type || 'undefined'),
          paragraphCount: validatedContentArray.filter((item: any) => item?.type === 'paragraph').length,
          headingCount: validatedContentArray.filter((item: any) => item?.type === 'heading').length,
          listCount: validatedContentArray.filter((item: any) => item?.type === 'bullet_list' || item?.type === 'ordered_list').length,
          otherTypes: validatedContentArray.filter((item: any) => item && item.type && !['paragraph', 'heading', 'bullet_list', 'ordered_list', 'blockquote', 'code_block', 'table'].includes(item.type)).map((item: any) => item.type),
          phase: 'final_content_array'
        });
        
        // 🔍 [INDENT_DEBUG] Check for indentation in final content array
        const itemsWithIndent = validatedContentArray.filter((item: any) => {
          if (item?.type === 'paragraph' || item?.type === 'heading') {
            return /^\s+/.test(item.text || '');
          }
          return false;
        });
        
        if (itemsWithIndent.length > 0) {
          console.log('✅ [INDENT_DEBUG] [ContentArray-FINAL] Items with leading spaces found:', {
            count: itemsWithIndent.length,
            samples: itemsWithIndent.slice(0, 5).map((item: any) => ({
              type: item.type,
              leadingSpaces: (item.text || '').match(/^\s+/)?.[0].length || 0,
              textPreview: JSON.stringify((item.text || '').substring(0, 60)),
              hasFormatting: !!(item.formatting && item.formatting.length > 0),
              formattingRanges: item.formatting?.length || 0
            })),
            note: 'These items should render with indentation in the document'
          });
        } else {
          console.log('⚠️ [INDENT_DEBUG] [ContentArray-FINAL] NO items with leading spaces in final array:', {
            totalItems: validatedContentArray.length,
            paragraphSamples: validatedContentArray
              .filter((item: any) => item?.type === 'paragraph')
              .slice(0, 3)
              .map((item: any) => ({
                text: JSON.stringify((item.text || '').substring(0, 50)),
                startsWithSpace: /^\s/.test(item.text || ''),
                firstChar: item.text ? item.text.charCodeAt(0) : 'none'
              }))
          });
        }
        
        const formattedCount = validatedContentArray.filter(c => hasText(c) && c.formatting && c.formatting.length > 0).length;
        if (formattedCount > 0) {
          console.log('✅ [DOC_WRITE] ContentArray complete:', {
            totalItems: validatedContentArray.length,
            withFormatting: formattedCount
          });
        }
        
        return validatedContentArray;
      };

      // 🆕 NEW: Export markdownToContentArray globally for streaming use
      // This allows the streaming service to reuse the exact same conversion logic
      // Reference: doc_write.ts lines 512-954 (markdownToContentArray function)
      (window as any).__docWriteStreaming = {
        markdownToContentArray
      };
      
      log.log(`🎬 [DOC_WRITE_EXPORT] Exported markdownToContentArray to window.__docWriteStreaming`, {
        timestamp: new Date().toISOString(),
        functionType: typeof markdownToContentArray,
        exported: 'markdownToContentArray'
      });

      // Helper: resolve a contiguous span via facade (disallow ALL/__ALL__/node_root tokens and enforce small sections)
      // ✅ ENHANCED VALIDATION: Check node ID formats early
      if (Array.isArray(replaceNodes)) {
        for (const nodeId of replaceNodes) {
          const validation = DocumentUtils.validateNodeId(nodeId);
          if (!validation.isValid) {
            return createErrorResult(
              'Invalid node ID format',
              validation.error || `Invalid node ID: ${nodeId}`,
              'validation',
              { metadata: { executionTime: Date.now() - startTime } }
            );
          }
        }
      }
      
      if (fromNode) {
        const validation = DocumentUtils.validateNodeId(fromNode);
        if (!validation.isValid) {
          return createErrorResult(
            'Invalid fromNode ID format',
            validation.error || `Invalid fromNode ID: ${fromNode}`,
            'validation',
            { metadata: { executionTime: Date.now() - startTime } }
          );
        }
      }
      
      if (toNode) {
        const validation = DocumentUtils.validateNodeId(toNode);
        if (!validation.isValid) {
          return createErrorResult(
            'Invalid toNode ID format',
            validation.error || `Invalid toNode ID: ${toNode}`,
            'validation',
            { metadata: { executionTime: Date.now() - startTime } }
          );
        }
      }

      const resolveSpan = (ids?: string[], fromId?: string, toId?: string, a?: any, options?: { contiguous?: boolean }): { fromId: string; toId: string; rangeIds: string[] } | null => {
        const isAllToken = (s: any) => typeof s === 'string' && ['ALL', '__ALL__', 'node_root'].includes(String(s));
        if (ids && Array.isArray(ids)) {
          if (ids.length === 0) return null;
          if (ids.some(isAllToken)) {
            console.error('🚨 [DOC_WRITE_ANTI_PATTERN] Blocked attempt to use full document tokens:', ids);
            return null;
          }
          // ✅ SOFT GUIDANCE: Warn about large replacements but allow them
          if (ids.length > 10) {
            console.warn(`⚠️ [DOC_WRITE_LARGE_EDIT] Large edit detected (soft guidance):`, {
              requestedNodeCount: ids.length,
              recommendation: 'Consider progressive editing for better quality',
              willProceed: true,
              aiGuidance: 'Large edit allowed - AI decision respected'
            });
          }
        }
        if (isAllToken(fromId) || isAllToken(toId)) {
          console.error('🚨 [DOC_WRITE_ANTI_PATTERN] Blocked attempt to use full document range tokens:', { fromId, toId });
          return null;
        }
        
        // ✅ ENHANCED: Pass contiguous parameter to resolveSpanFacade
        const span = resolveSpanFacade({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any, { 
          ids, 
          fromId, 
          toId, 
          anchors: a,
          contiguous: options?.contiguous // Pass through the contiguous parameter
        });
        
        if (!span) return null;
        const r = span as any;
        
        // ✅ SOFT GUIDANCE: Warn about very large spans but allow them
        if (r.rangeIds && r.rangeIds.length > 15) {
          console.warn('⚠️ [DOC_WRITE_LARGE_SPAN] Very large span detected (soft guidance):', {
            resolvedNodeCount: r.rangeIds.length,
            recommendation: 'Consider smaller sections for better quality',
            willProceed: true
          });
        }
        
        return { fromId: r.rangeIds[0], toId: r.rangeIds[r.rangeIds.length - 1], rangeIds: r.rangeIds };
      };

      // Decide operation by intent
      const hasReplaceTarget = Array.isArray(replaceNodes) || (fromNode && toNode) || selectAll || (anchors && (anchors.startText || anchors.endText));
      const hasRemoveTarget = Array.isArray(removeNodes) || (fromNode && toNode) || selectAll || (anchors && (anchors.startText || anchors.endText));

      // Enforce pairing rules (Markdown-only):
      // 🚨 DEBUG: Add comprehensive operation detection logging
      log.log('🔍 [DOC_WRITE_OPERATION_DEBUG] === OPERATION DETECTION START ===', {
        timestamp: new Date().toISOString(),
        toolUseId: context.executionId,
        documentId: id,
        inputParameters: {
          hasMarkdownContent: !!markdownContent,
          hasInsertLocation: !!insertLocation,
          hasReplaceTarget,
          hasRemoveTarget,
          hasRevisionId: !!revisionId,
          markdownContentLength: markdownContent?.length || 0,
          insertLocationKeys: insertLocation ? Object.keys(insertLocation) : null,
          insertLocationValue: insertLocation
        }
      });

      // - REPLACE: markdownContent + explicit replace target only
      // - INSERT: markdownContent + explicit insertLocation only (requires before/after anchor)
      // - REMOVE: explicit remove target only
      const wantsReplace = !!markdownContent && hasReplaceTarget && !insertLocation && !revisionId;
      const wantsInsert = !!markdownContent && !!insertLocation && !hasReplaceTarget && !revisionId;
      const wantsRemove = !markdownContent && hasRemoveTarget && !insertLocation && !revisionId;
      const wantsRevisionRedo = !!markdownContent && !!revisionId && !insertLocation && !hasReplaceTarget && !hasRemoveTarget;

      log.log('🔍 [DOC_WRITE_OPERATION_DEBUG] === OPERATION INTENT DETECTION ===', {
        timestamp: new Date().toISOString(),
        operationIntents: {
          wantsReplace,
          wantsRemove,
          wantsInsert,
          wantsRevisionRedo
        },
        operationsCount: [wantsReplace, wantsInsert, wantsRemove, wantsRevisionRedo].filter(Boolean).length
      });

      // Guard against ambiguous/mixed parameter sets strictly by computed intents
      const operationsCount = [wantsReplace, wantsInsert, wantsRemove, wantsRevisionRedo].filter(Boolean).length;
      if (operationsCount > 1) {
        return createErrorResult(
          'Ambiguous parameters',
          'Do not mix REPLACE, INSERT, and REMOVE parameters. Use one operation per call.',
          'validation'
        );
      }

      if (!wantsReplace && !wantsRemove && !wantsInsert && !wantsRevisionRedo) {
        // 🚨 CRITICAL: Block implicit full document replacement - force explicit targeting
        if (markdownContent) {
          return createErrorResult(
            'Missing explicit target for replacement',
            `🚨 MISSING NODE IDS: Document "${id}" needs exact node IDs for editing

🔍 DIAGNOSIS:
- You have content to write but no replaceNodes specified
- This often means document is in context but node IDs weren't used from availableNodeIDs

✅ SOLUTION:
1. Check context for availableNodeIDs: [...] - use these exact IDs
2. Use: replaceNodes: ["exact_node_id_1", "exact_node_id_2", ...]
3. If no availableNodeIDs in context → call doc_read_v2 first

❌ FORBIDDEN: Guessing node IDs like "node_root", "ALL" causes failures
✅ REQUIRED: Use exact node IDs from context or doc_read_v2 output`,
            'validation'
          );
        }
        return createErrorResult(
          'Missing operation type',
          `You provided markdownContent but did not specify an operation type.

**You must choose ONE:**
- REPLACE: Provide replaceNodes (array of node IDs to replace)
- INSERT: Provide insertLocation (where to insert: {"at": "start"}, {"after": "node_id"}, etc.)
- REMOVE: Provide removeNodes (don't provide markdownContent for remove)

**What you provided:** ${Object.keys(params).join(', ')}
**Missing:** One of: replaceNodes, insertLocation, or removeNodes`,
          'validation'
        );
      }

      // 0) REVISION REDO (Markdown) — replace content of nodes that share a prior revision_id
      if (wantsRevisionRedo) {
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Locating revision to redo...' }); } catch {}
        const all = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const nodes: any[] = all.nodes || [];
        const matchedIds: string[] = [];
        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i];
          const rid = (n && n.attrs && (n.attrs.revision_id || n.attrs.revisionId)) || undefined;
          if (rid === revisionId) {
            matchedIds.push(n.id);
          }
        }
        if (matchedIds.length === 0) {
          return createErrorResult('Revision not found', `No nodes with revision_id ${revisionId} were found`, 'validation');
        }

        // Ensure matched nodes are contiguous by checking they form a consecutive sequence
        const matchedIndices = matchedIds.map(id => nodes.findIndex(n => n.id === id)).sort((a, b) => a - b);
        for (let i = 1; i < matchedIndices.length; i++) {
          if (matchedIndices[i] !== matchedIndices[i - 1] + 1) {
            return createErrorResult('Non-contiguous revision', `Nodes with revision_id ${revisionId} are not contiguous in the document`, 'validation');
          }
        }

        const fromId = matchedIds[0];
        const toId = matchedIds[matchedIds.length - 1];

        traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_write', op: 'revision_redo', targetCount: matchedIds.length, revisionId } });
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Applying revision redo...' }); } catch {}
        const resolvedRevisionMarkdown = await preFetchImagesInMarkdown(String(markdownContent));
        const resp = replaceMarkdown(
          { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any,
          { from: fromId, to: toId },
          resolvedRevisionMarkdown,
          { batchDescription: 'Replace', restrictIds: matchedIds, suggestionMode: useSuggestionMode }
        );
        if (!resp || (resp as any).ok === false) {
          return createErrorResult('Revision redo failed', 'Failed to replace content for the specified revision', 'execution');
        }

        const insertedIds = (resp as any).insertedIds || [];
        const allAfter = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const nodesAfter: any[] = allAfter.nodes || [];
        const newNodes = nodesAfter.filter((n: any) => insertedIds.includes(n.id));

        traceService.log({
          traceId: effectiveTraceId,
          type: 'tool_success',
          data: { tool: 'doc_write', op: 'revision_redo', replacedCount: matchedIds.length }
        });

        // ✅ ENHANCED: Generate contextual completion message for revision redo
        const contextualMessage = generateContextualMessage({
          operation: 'replace',
          nodeCount: matchedIds.length,
          markdownContent: String(markdownContent),
          newNodes
        });

        const success = createSuccessResult(
          `Revised content (revision ${revisionId}) - ${contextualMessage.toLowerCase()}`,
          {
            type: 'document_revision_redo',
            oldRevisionId: revisionId,
            newRevisionId: (resp as any).revisionId,
            replacedCount: matchedIds.length,
            replacedNodeIds: matchedIds,
            insertedNodeIds: insertedIds,
            newNodes,
            // ✅ NEW: Enhanced message context for revision operations
            messageContext: {
              fullContent: String(markdownContent), // ← Save full content for UI display
              contentPreview: String(markdownContent).substring(0, 100) + (String(markdownContent).length > 100 ? '...' : ''),
              operationSummary: `Revised existing content (revision ${revisionId})`,
              revisedCount: matchedIds.length,
              revisionId: (resp as any).revisionId
            }
          },
          { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: 'revision_redo' } as any }
        );
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...' }); } catch {}
        // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
        await forceFlushDocumentSave(doc.docId);
        return success;
      }

      // 1) REPLACE (Markdown) - Enhanced with structure preservation
      if (wantsReplace) {
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Computing replace span...' }); } catch {}
        
        // ✅ NEW: Remove any streaming placeholder to prevent double-writes
        try {
          const yxmlArray = doc.yxml.toArray();
          const placeholderIndex = yxmlArray.findIndex((el: any) => {
            if (typeof el.getAttribute === 'function') {
              const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
              const isFailed = el.getAttribute('data-streaming-failed') === 'true';
              return isActiveStreaming || isFailed;
            }
            return false;
          });
          
          if (placeholderIndex !== -1) {
            const placeholderEl = yxmlArray[placeholderIndex] as any;
            const placeholderId = placeholderEl && typeof placeholderEl.getAttribute === 'function' 
              ? placeholderEl.getAttribute('id') 
              : 'unknown';
            
            log.log(`🧹 [INSERT_CLEANUP] Found streaming placeholder at index ${placeholderIndex}, removing`, {
              yxmlLength: yxmlArray.length,
              placeholderId
            });
            
            doc.yDoc.transact(() => {
              doc.yxml.delete(placeholderIndex, 1);
            }, { source: 'cleanup-streaming-placeholder' });
            
            // Verify deletion
            const yxmlArrayAfter = doc.yxml.toArray();
            const stillExists = yxmlArrayAfter.some((el: any) => {
              if (el && typeof el.getAttribute === 'function' && el.nodeName) {
                const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
                const isFailed = el.getAttribute('data-streaming-failed') === 'true';
                return isActiveStreaming || isFailed;
              }
              return false;
            });
            
            if (stillExists) {
              console.error(`❌ [INSERT_CLEANUP_ERROR] Placeholder still exists after deletion!`);
              throw new Error('Placeholder deletion verification failed');
            }
            
            log.log(`✅ [INSERT_CLEANUP] Placeholder deleted successfully`, {
              yxmlLengthBefore: yxmlArray.length,
              yxmlLengthAfter: yxmlArrayAfter.length
            });
          } else {
            log.log(`ℹ️ [INSERT_CLEANUP] No streaming placeholder found`, {
              yxmlLength: yxmlArray.length
            });
          }
        } catch (cleanupError) {
          console.error(`❌ [INSERT_CLEANUP_ERROR] Error cleaning placeholder:`, {
            error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
            stack: cleanupError instanceof Error ? cleanupError.stack : undefined
          });
          throw cleanupError;  // ✅ FAIL if cleanup fails
        }
        
        // ✅ NEW: Analyze original structure before replacement for preservation hints
        let originalStructureInfo: any = null;
        try {
          const allNodes = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
          if (allNodes.nodes && Array.isArray(replaceNodes)) {
            const targetNodes = allNodes.nodes.filter((n: any) => replaceNodes.includes(n.id));
            originalStructureInfo = {
              hasLists: targetNodes.some((n: any) => n.type === 'ordered_list' || n.type === 'bullet_list'),
              hasNestedLists: targetNodes.some((n: any) => n.type === 'list_item' && n.attributes?.content),
              listStructure: targetNodes.filter((n: any) => 
                n.type === 'ordered_list' || n.type === 'bullet_list' || n.type === 'list_item'
              ).map((n: any) => ({
                id: n.id,
                type: n.type,
                attributes: n.attributes,
                parentType: targetNodes.find((p: any) => 
                  p.attributes?.content && Array.isArray(p.attributes.content) &&
                  p.attributes.content.some((c: any) => c.id === n.id)
                )?.type
              })),
              originalNodeTypes: targetNodes.map((n: any) => ({ id: n.id, type: n.type }))
            };
            
            log.log('🔧 [STRUCTURE_PRESERVATION] Analyzed original structure:', {
              targetNodeCount: targetNodes.length,
              hasLists: originalStructureInfo.hasLists,
              hasNestedLists: originalStructureInfo.hasNestedLists,
              listCount: originalStructureInfo.listStructure.length
            });
          }
        } catch (error) {
          console.warn('[STRUCTURE_PRESERVATION] Failed to analyze original structure:', error);
        }
        
        // Resolve target span; no implicit full-document fallback. Only accept explicit spans
        log.log('🔍 [DOC_WRITE_REPLACE_DEBUG] === REPLACE OPERATION SPAN RESOLUTION ===', {
          timestamp: new Date().toISOString(),
          replaceNodes: replaceNodes,
          fromNode,
          toNode,
          anchors,
          useContiguous: true // Replace operations need range expansion for proper content replacement
        });
        
        // ✅ Use contiguous parameter with smart defaults
        const useContiguous = params.contiguous !== undefined ? params.contiguous : true; // Default true for replace
        
        log.log('🔍 [CONTIGUOUS_DEBUG] === CONTIGUOUS MODE SELECTION ===', {
          operation: 'replace',
          explicitContiguous: params.contiguous,
          defaultContiguous: true,
          finalContiguous: useContiguous
        });
        
        const spanResolvedMaybe = selectAll
          ? resolveAllSpan()
          : resolveSpan(replaceNodes, fromNode, toNode, anchors, {
              contiguous: useContiguous
            });
        
        log.log('🔍 [DOC_WRITE_REPLACE_DEBUG] === REPLACE SPAN RESOLUTION RESULT ===', {
          timestamp: new Date().toISOString(),
          requestedNodes: replaceNodes,
          resolvedSpan: spanResolvedMaybe,
          resolvedNodeCount: spanResolvedMaybe?.rangeIds?.length || 0,
          resolvedNodes: spanResolvedMaybe?.rangeIds || [],
          rangeExpansionUsed: useContiguous,
          contiguousMode: useContiguous ? 'range_expansion' : 'exact_nodes_only'
        });
        
        // Validate contiguous choice for potential misuse
        if (!useContiguous && replaceNodes && replaceNodes.length > 1) {
          console.warn('🚨 [CONTIGUOUS_WARNING] Non-contiguous replace detected', {
            requestedNodes: replaceNodes.length,
            resolvedNodes: spanResolvedMaybe?.rangeIds?.length || 0,
            warning: 'Non-contiguous replace may leave gaps in content. Consider contiguous:true for section replacement.'
          });
        }
        // Clear error for {at: "all"} on an empty document
        if (selectAll && !spanResolvedMaybe) {
          return createErrorResult('Document is empty', 'Cannot replace: the document has no content. Use insert with {at: "start"} to add content.', 'validation');
        }
        // 🚨 CRITICAL: NO LONGER ALLOW full-document replacement via sentinel tokens
        // Force the AI to use explicit node IDs for iterative editing
        if (!spanResolvedMaybe) {
          const isAllToken = (s: any) => typeof s === 'string' && ['ALL', '__ALL__', 'node_root'].includes(String(s));
          const idsArray: string[] | undefined = Array.isArray(replaceNodes) ? replaceNodes as string[] : undefined;
          const allRequested = (idsArray && idsArray.length === 0) || (idsArray && idsArray.length === 1 && isAllToken(idsArray[0])) || (isAllToken(fromNode) && isAllToken(toNode));
          if (allRequested) {
            console.warn('⚠️ [DOC_WRITE_FULL_DOC] Full document replacement detected (soft guidance):', {
              replaceNodes: idsArray,
              fromNode,
              toNode,
              recommendation: 'Consider progressive editing for large documents',
              willProceed: true
            });
            // ✅ ALLOW the operation to proceed - just log the warning
          }
        }
        if (!spanResolvedMaybe) {
          // Enhanced error message with debugging information
          const debugInfo = {
            requestedNodes: replaceNodes || [],
            fromNode,
            toNode,
            suggestion: 'Use doc_read_v2 first to get valid node IDs from the document'
          };
          
          // Try to get available node IDs for better error reporting
          let availableNodeIds: string[] = [];
          
          // 🔍 PRIORITY: Use fresh node IDs if available (from reference-only mode)
          if ((context as any).freshAvailableNodeIds && Array.isArray((context as any).freshAvailableNodeIds)) {
            availableNodeIds = (context as any).freshAvailableNodeIds.slice(0, 10);
            log.log(`🔍 [ERROR_REPORTING] Using fresh node IDs from reference-only fetch:`, {
              nodeIds: availableNodeIds,
              source: 'fresh_content_resolver'
            });
          } else {
            // Fallback: Try to read from document
            try {
              const allNodesResult = readAllDoc(doc as any);
              if (allNodesResult?.nodes && Array.isArray(allNodesResult.nodes)) {
                availableNodeIds = allNodesResult.nodes.map((node: any) => node.id).filter(Boolean).slice(0, 10);
              }
            } catch (readError) {
              // Ignore read errors for debugging
            }
          }
          
          const enhancedMessage = availableNodeIds.length > 0 
            ? `Could not resolve target nodes. Available node IDs in document: ${availableNodeIds.join(', ')}${availableNodeIds.length === 10 ? '...' : ''}. Use these exact node IDs for targeting. For scattered formatting edits, try contiguous: false. For section replacement, use contiguous: true or omit.`
            : `Could not resolve a contiguous span to replace. Document may be empty or node IDs may have changed. For scattered formatting edits, try contiguous: false. For section replacement, use contiguous: true or omit. Current: contiguous=${useContiguous}`;
            
            return createErrorResult(
              'Invalid replace target', 
              enhancedMessage,
              'validation',
              { 
                metadata: { 
                  executionTime: Date.now() - startTime,
                  method: 'doc_write',
                  affectedEntities: [doc.docId]
                } 
              }
            );
        }
        const spanResolved = spanResolvedMaybe;
        const spanNonNull: { fromId: string; toId: string; rangeIds: string[] } = spanResolved;

        // 🚨 ADDITIONAL VALIDATION: Warn about large replacements and suggest iterative approach
        if (spanNonNull.rangeIds.length > 10) {
          console.warn('⚠️ [DOC_WRITE_LARGE_SECTION] Large section replacement detected:', {
            nodeCount: spanNonNull.rangeIds.length,
            recommendation: 'Consider breaking this into smaller sections for better iterative editing',
            nodeIds: spanNonNull.rangeIds.slice(0, 5).concat(['...'])
          });
        }
        
        // ✅ SOFT GUIDANCE: Warn about large replacements but don't block
        if (spanNonNull.rangeIds.length > 10) {
          console.warn('⚠️ [DOC_WRITE_LARGE_OPERATION] Large replacement detected (soft guidance):', {
            nodeCount: spanNonNull.rangeIds.length,
            recommendation: 'Consider progressive editing for better quality',
            firstFewNodes: spanNonNull.rangeIds.slice(0, 5),
            suggestedPattern: `Progressive: 1st call: 3-4 nodes, 2nd call: 5-6 nodes, continue until complete`,
            willProceed: true
          });
        }
        
        log.log('✅ [DOC_WRITE_FLEXIBLE] Processing replacement with soft guidance:', {
          nodeCount: spanNonNull.rangeIds.length,
          isLargeOperation: spanNonNull.rangeIds.length > 10,
          firstFewNodes: spanNonNull.rangeIds.slice(0, 5),
          message: `Processing ${spanNonNull.rangeIds.length} nodes as requested by AI`
        });

        traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_write', op: 'replace', targetCount: spanNonNull.rangeIds.length } });

        // 🚨 CRITICAL DEBUG: Pre-edit analysis - what does Claude think it's editing?
        log.log('🎯 [CLAUDE_EDIT_DEBUG] === PRE-EDIT ANALYSIS ===', {
          operation: 'replace',
          documentId: id,
          targetNodeIds: spanNonNull.rangeIds,
          targetNodeCount: spanNonNull.rangeIds.length,
          fromNodeId: spanNonNull.fromId,
          toNodeId: spanNonNull.toId,
          claudeIntendedContent: {
            contentPreview: String(markdownContent).substring(0, 200),
            contentSections: String(markdownContent).split('\n').filter(line => line.startsWith('#')).slice(0, 3),
            contentLength: String(markdownContent).length
          },
          timestamp: new Date().toISOString()
        });
        
        // 🚨 CRITICAL DEBUG: What content is ACTUALLY in the target nodes right now?
        try {
          const preEditContent = spanNonNull.rangeIds.map((nodeId: string, index: number) => {
            try {
              const location = doc.yxml ? 
                require('../../services/doc/internal/yDocHighLevelAPI').DocumentUtils.findNodeById(doc.yxml, nodeId) : 
                null;
              
              if (location) {
                // 🚨 CRITICAL FIX: DO NOT use location.element.toString() - causes fontFamily contamination!
                // This is in the main AI command used by Claude - contamination here affects all AI edits
                let textContent = '[debug text hidden - toString() disabled to prevent XML contamination]';
                try {
                  const anyElement = location.element as any;
                  if (anyElement._content && typeof anyElement._content === 'string') {
                    textContent = anyElement._content.substring(0, 150) || '[No text content]';
                  }
                } catch {
                  textContent = '[No text content]';
                }
                const changeStatus = location.element.getAttribute('change_status') || 'none';
                const nodeType = location.element.nodeName || 'unknown';
                
                return {
                  nodeId,
                  index: location.index,
                  actualContent: textContent.substring(0, 150),
                  nodeType,
                  changeStatus,
                  isOldSuggestion: changeStatus === 'old',
                  isNewSuggestion: changeStatus === 'new',
                  targetIndex: index
                };
              }
              return { nodeId, error: 'node_not_found', targetIndex: index };
            } catch (error) {
              return { nodeId, error: error instanceof Error ? error.message : 'unknown_error', targetIndex: index };
            }
          });
          
          log.log('🔍 [CLAUDE_EDIT_DEBUG] === ACTUAL CONTENT IN TARGET NODES ===', {
            preEditNodes: preEditContent,
            nodesFound: preEditContent.filter(n => !n.error).length,
            nodesMissing: preEditContent.filter(n => n.error).length,
            oldSuggestions: preEditContent.filter(n => n.isOldSuggestion).length,
            newSuggestions: preEditContent.filter(n => n.isNewSuggestion).length,
            contentMismatchWarning: preEditContent.some(n => n.actualContent && !n.actualContent.toLowerCase().includes('casting')),
            timestamp: new Date().toISOString()
          });
          
          // 🚨 ALERT: Check if Claude is targeting the wrong section
          const castingRelatedNodes = preEditContent.filter(n => 
            n.actualContent && (
              n.actualContent.toLowerCase().includes('casting') ||
              n.actualContent.toLowerCase().includes('bronze') ||
              n.actualContent.toLowerCase().includes('brass') ||
              n.actualContent.toLowerCase().includes('mold')
            )
          );
          
        } catch (debugError) {
          console.error('❌ [CLAUDE_EDIT_DEBUG] Failed to analyze pre-edit content:', debugError);
        }

        try { context.onProgress?.({ type: 'streaming_update', activity: 'Applying markdown replacement...' }); } catch {}
        
        // ✅ NEW: Enhanced markdown replacement with structure preservation hints
        let enhancedMarkdown = String(markdownContent);
        
        // 🧹 PRE-NORMALIZATION: Analyze trailing newlines BEFORE normalization
        const trailingMatch = String(markdownContent).match(/\n+$/);
        const trailingNewlineCount = trailingMatch ? trailingMatch[0].length : 0;
        
        log.log(`🔴 [REPLACE_BEFORE_NORMALIZE] Incoming markdown trailing newlines`, {
          totalLength: String(markdownContent).length,
          lastCharCode: String(markdownContent).charCodeAt(String(markdownContent).length - 1),
          lastCharCodes: Array.from(String(markdownContent).slice(-20)).map(c => c.charCodeAt(0)),
          trailingNewlineCount: trailingNewlineCount,
          trailingNewlinePreview: JSON.stringify(String(markdownContent).slice(-15))
        });
        
        // 🚨 CRITICAL: Normalize markdown IMMEDIATELY to remove leading/trailing newlines
        // This prevents "massive amounts of newlines BEFORE and AFTER" in the final tool output
        // Note: ReplaceWithMarkdown.execute() will normalize again, but this catches it early
        const { cleaned: trimmedMarkdown } = MarkdownNormalizer.normalize(enhancedMarkdown);
        enhancedMarkdown = trimmedMarkdown;
        
        // 🧹 POST-NORMALIZATION: Verify trailing newlines AFTER normalization
        const postTrailingMatch = enhancedMarkdown.match(/\n+$/);
        const postTrailingNewlineCount = postTrailingMatch ? postTrailingMatch[0].length : 0;
        
        log.log(`🧹 [REPLACE_MARKDOWN_TRIM] Normalized markdown before replacement`, {
          originalLength: String(markdownContent).length,
          trimmedLength: enhancedMarkdown.length,
          hadLeadingNewlines: /^\n+/.test(String(markdownContent)),
          hadTrailingNewlines: /\n+$/.test(String(markdownContent)),
          trailingNewlinesBefore: trailingNewlineCount,
          trailingNewlinesAfter: postTrailingNewlineCount,
          preview: `"${enhancedMarkdown.substring(0, 60)}..."`
        });
        
        // 🔴 FINAL CHECK: If still has trailing newlines, log them
        if (postTrailingNewlineCount > 0) {
          console.error(`🚨 [REPLACE_STILL_HAS_TRAILING_NEWLINES] Normalization failed to remove all trailing newlines!`, {
            count: postTrailingNewlineCount,
            lastChars: JSON.stringify(enhancedMarkdown.slice(-20)),
            willPassToReplace: true
          });
        }
        
        const replacementOptions: any = { 
          batchDescription: 'Replace', 
          restrictIds: spanNonNull.rangeIds, 
          suggestionMode: useSuggestionMode // ✅ Auto-accept in workflow mode, suggestions otherwise
        };
        
        // ✅ DEBUG: Log the raw markdown content we're receiving
        log.log('🔧 [STRUCTURE_PRESERVATION] Raw AI content analysis:', {
          contentLength: enhancedMarkdown.length,
          contentPreview: enhancedMarkdown.substring(0, 300),
          hasListMarkers: enhancedMarkdown.includes('1. ') || enhancedMarkdown.includes('- '),
          hasBoldMarkers: enhancedMarkdown.includes('**'),
          hasHtmlTags: enhancedMarkdown.includes('<'),
          legalKeywordCount: ['Petitioners', 'Trustee', 'Beneficiaries', 'Notice'].filter(k => enhancedMarkdown.includes(k)).length
        });
        
        // 🚨 CRITICAL DEBUG: Log what Claude is trying to edit and where
        log.log('🎯 [CLAUDE_EDIT_DEBUG] === CLAUDE EDIT ANALYSIS ===', {
          operation: 'replace',
          targetNodeIds: spanNonNull.rangeIds,
          targetNodeCount: spanNonNull.rangeIds.length,
          documentId: id,
          contentPreview: enhancedMarkdown.substring(0, 200),
          contentSections: enhancedMarkdown.split('\n').filter(line => line.startsWith('#')).slice(0, 5),
          timestamp: new Date().toISOString()
        });
        
        // 🚨 CRITICAL DEBUG: Verify what content is CURRENTLY in the target nodes
        try {
          const currentContent = spanNonNull.rangeIds.map((nodeId: string) => {
            try {
              const location = doc.yxml ? 
                require('../../services/doc/internal/yDocHighLevelAPI').DocumentUtils.findNodeById(doc.yxml, nodeId) : 
                null;
              
              if (location) {
                // 🚨 CRITICAL FIX: DO NOT use location.element.toString() - causes fontFamily contamination!
                // This is in the main AI command used by Claude - contamination here affects all AI edits
                let textContent = '[debug text hidden - toString() disabled to prevent XML contamination]';
                try {
                  const anyElement = location.element as any;
                  if (anyElement._content && typeof anyElement._content === 'string') {
                    textContent = anyElement._content.substring(0, 150) || '[No text content]';
                  }
                } catch {
                  textContent = '[No text content]';
                }
                return {
                  nodeId,
                  index: location.index,
                  currentContent: textContent.substring(0, 100),
                  nodeType: location.element.nodeName,
                  hasContent: textContent.length > 0
                };
              }
              return { nodeId, error: 'node_not_found' };
            } catch (error) {
              return { nodeId, error: error instanceof Error ? error.message : 'unknown_error' };
            }
          });
          
          log.log('🔍 [CLAUDE_EDIT_DEBUG] === CURRENT CONTENT IN TARGET NODES ===', {
            targetNodes: currentContent,
            totalTargetNodes: spanNonNull.rangeIds.length,
            nodesFound: currentContent.filter(n => !n.error).length,
            nodesMissing: currentContent.filter(n => n.error).length,
            timestamp: new Date().toISOString()
          });
        } catch (debugError) {
          console.error('❌ [CLAUDE_EDIT_DEBUG] Failed to analyze current content in target nodes:', debugError);
        }
        
        // Add structure preservation hints if original content had complex formatting
        if (originalStructureInfo?.hasLists || originalStructureInfo?.hasNestedLists) {
          log.log('🔍 [LOSSLESS_CONVERSION] Processing Claude\'s markdown as-is');
          
          // ✅ ENHANCED: First, convert HTML markup to proper markdown
          const cleanMarkdown = enhancedMarkdown
            .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
            .replace(/<em>(.*?)<\/em>/g, '*$1*')
            .replace(/<code>(.*?)<\/code>/g, '`$1`')
            .replace(/<u>(.*?)<\/u>/g, '$1') // Remove underline tags
            .replace(/<[^>]+>/g, ''); // Remove any other HTML tags
          
          log.log('🔧 [STRUCTURE_PRESERVATION] Converted HTML to markdown:', {
            originalLength: enhancedMarkdown.length,
            cleanedLength: cleanMarkdown.length,
            hadHtmlTags: enhancedMarkdown.includes('<'),
            preview: cleanMarkdown.substring(0, 200)
          });
          
          // Simply use the markdown as-is - no hacky structure preservation
          enhancedMarkdown = cleanMarkdown;
        }
        
        const hasRawMarkdownSyntax = enhancedMarkdown.match(/^#{1,6}\s+/m) || 
                                      enhancedMarkdown.match(/^\*\*[^*]+\*\*$/m) ||
                                      enhancedMarkdown.match(/^[-*]\s+/m);
        
        if (hasRawMarkdownSyntax) {
          log.log('✅ [MARKDOWN_SAFETY] Markdown contains formatting syntax - will be converted to elements:', {
            hasHeadings: !!enhancedMarkdown.match(/^#{1,6}\s+/m),
            hasBold: !!enhancedMarkdown.match(/^\*\*[^*]+\*\*$/m),
            hasLists: !!enhancedMarkdown.match(/^[-*]\s+/m),
            markdownPreview: enhancedMarkdown.substring(0, 100)
          });
        }
        
        // 🔥 FINAL DEBUG: Show EXACT markdown being passed to replaceMarkdown
        console.error('🔥 [MARKDOWN_FINAL_CHECK_BEFORE_REPLACE] === EXACT MARKDOWN TO BE REPLACED ===', {
          totalLength: enhancedMarkdown.length,
          lastCharCode: enhancedMarkdown.charCodeAt(enhancedMarkdown.length - 1),
          endsWithNewline: enhancedMarkdown.endsWith('\n'),
          startsWithNewline: enhancedMarkdown.startsWith('\n'),
          trailingNewlines: (enhancedMarkdown.match(/\n+$/) || [])[0]?.length || 0,
          leadingNewlines: (enhancedMarkdown.match(/^\n+/) || [])[0]?.length || 0,
          lines: enhancedMarkdown.split('\n').length,
          lastLine: enhancedMarkdown.split('\n')[enhancedMarkdown.split('\n').length - 1],
          lastLineLength: enhancedMarkdown.split('\n')[enhancedMarkdown.split('\n').length - 1].length,
          lastLineCharCodes: Array.from(enhancedMarkdown.split('\n')[enhancedMarkdown.split('\n').length - 1]).map(c => c.charCodeAt(0)),
          FULL_MARKDOWN: enhancedMarkdown  // Show full text
        });
        
        // Pre-fetch any grok: or unsplash: image URLs before the markdown pipeline runs
        enhancedMarkdown = await preFetchImagesInMarkdown(enhancedMarkdown);

        // 🆕 STREAMING: Show live preview of replacement content
        try {
          const progressData = {
            type: 'live_preview',
            message: `Replacing ${spanNonNull.rangeIds.length} section(s)...`,
            liveMarkdown: enhancedMarkdown,
            position: `nodes ${spanNonNull.rangeIds[0].substring(5, 20)}...${spanNonNull.rangeIds[spanNonNull.rangeIds.length-1].substring(5, 20)}`,
            isStreaming: true
          };
          
          log.log('[doc_write] 🎬 Emitting live preview (replace):', {
            nodeCount: spanNonNull.rangeIds.length,
            markdownLength: enhancedMarkdown.length,
            position: progressData.position
          });
          
          context.onProgress?.(progressData);
          await new Promise(resolve => setTimeout(resolve, 500));
          log.log('[doc_write] ✅ Preview delay complete, committing replacement');
        } catch (err) {
          console.error('[doc_write] ❌ Preview emission failed:', err);
        }
        
        // 🔥 DEBUG: Log document state BEFORE replacement
        log.log('🔥 [DOC_STATE_BEFORE_REPLACE] === DOCUMENT STATE BEFORE REPLACEMENT ===', {
          yxmlLength: doc.yxml.length,
          spanFromId: spanNonNull.fromId,
          spanToId: spanNonNull.toId,
          rangeIds: spanNonNull.rangeIds,
          placeholderPresent: Array.from({ length: doc.yxml.length }).some((_, i) => {
            const el = doc.yxml.get(i);
            return el && typeof el.getAttribute === 'function' && el.getAttribute('data-streaming') === 'true';
          }),
          timestamp: new Date().toISOString()
        });
        
        const resp = replaceMarkdown(
          { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any,
          { from: spanNonNull.fromId, to: spanNonNull.toId },
          enhancedMarkdown,
          replacementOptions
        );
        
        // 🚨 CRITICAL LOGGING: Log the replaceMarkdown response IMMEDIATELY
        log.log('🎯 [REPLACE_MARKDOWN_RESPONSE] === IMMEDIATE RESPONSE CHECK ===', {
          hasResponse: !!resp,
          responseOk: (resp as any)?.ok,
          responseError: (resp as any)?.error,
          insertedIds: (resp as any)?.insertedIds,
          revisionId: (resp as any)?.revisionId,
          timestamp: new Date().toISOString()
        });
        
        // 🔥 DEBUG: Log document state AFTER replacement (if successful)
        if ((resp as any)?.ok) {
          log.log('🔥 [DOC_STATE_AFTER_REPLACE] === DOCUMENT STATE AFTER REPLACEMENT (SUCCESS) ===', {
            yxmlLength: doc.yxml.length,
            insertedCount: (resp as any)?.insertedIds?.length || 0,
            insertedIds: (resp as any)?.insertedIds,
            revisionId: (resp as any)?.revisionId,
            timestamp: new Date().toISOString()
          });
        } else {
          log.log('🔥 [DOC_STATE_AFTER_REPLACE] === DOCUMENT STATE AFTER REPLACEMENT (FAILED) ===', {
            yxmlLength: doc.yxml.length,
            error: (resp as any)?.error,
            timestamp: new Date().toISOString()
          });
        }
        
        if (!resp || !(resp as any).ok) {
          console.error('❌ [REPLACE_FAILED] replaceMarkdown returned error:', {
            resp,
            error: (resp as any)?.error,
            targetNodeIds: spanNonNull.rangeIds,
            fromNodeId: spanNonNull.fromId,
            toNodeId: spanNonNull.toId
          });
          return createErrorResult(
            'Replace operation failed',
            `Failed to replace nodes: ${(resp as any)?.error || 'Unknown error'}`,
            'execution',
            { metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId] } }
          );
        }
        
        // 🚨 POST-EXECUTION SAFETY CHECK: Verify no raw markdown in document
        // This runs AFTER YJS conversion to detect if markdown syntax leaked through
        try {
          const { readAll } = await import('../../services/doc/docApi');
          const afterContent = readAll({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: {} } as any);
          
          if (afterContent && afterContent.nodes) {
            const nodesWithRawMarkdown = afterContent.nodes.filter((node: any) => {
              const text = node.text || '';
              // Check for markdown heading syntax at start of text
              return /^#{1,6}\s+/.test(text) || /^##\s+/.test(text);
            });
            
            if (nodesWithRawMarkdown.length > 0) {
              console.error('🚨🚨🚨 [CRITICAL] RAW MARKDOWN DETECTED IN DOCUMENT AFTER CONVERSION!');
              console.error('🚨 Affected nodes:', {
                count: nodesWithRawMarkdown.length,
                nodeIds: nodesWithRawMarkdown.map((n: any) => n.id),
                samples: nodesWithRawMarkdown.slice(0, 3).map((n: any) => ({
                  nodeId: n.id,
                  type: n.type,
                  textPreview: (n.text || '').substring(0, 100)
                }))
              });
              console.error('🚨 This means markdown → YJS conversion FAILED for these nodes!');
              console.error('🚨 They contain raw markdown text instead of formatted heading elements!');
              console.error('🚨 INVESTIGATE: markdownTokensToYXml or marked.lexer parsing');
              
              // 🚨 CRITICAL: This is a DATA INTEGRITY issue - user will see raw markdown in editor
              // For now, we detect and log loudly. A correction mechanism would go here if needed.
                  } else {
              log.log('✅ [MARKDOWN_SAFETY] No raw markdown detected - conversion successful');
            }
          }
        } catch (safetyCheckError) {
          console.warn('⚠️ [MARKDOWN_SAFETY] Could not perform post-execution safety check:', safetyCheckError);
        }
        
        // 🚨 CRITICAL DEBUG: Log the results of the edit operation
        log.log('🎯 [CLAUDE_EDIT_DEBUG] === EDIT OPERATION RESULT ===', {
          operation: 'replace',
          success: !!(resp && (resp as any).ok),
          targetNodeIds: spanNonNull.rangeIds,
          targetNodeCount: spanNonNull.rangeIds.length,
          fromNodeId: spanNonNull.fromId,
          toNodeId: spanNonNull.toId,
          documentId: id,
          insertedIds: (resp as any)?.insertedIds || [],
          insertedCount: (resp as any)?.insertedIds?.length || 0,
          revisionId: (resp as any)?.revisionId,
          suggestionMode: replacementOptions.suggestionMode,
          timestamp: new Date().toISOString()
        });
        
        // 🚨 CRITICAL DEBUG: Verify what content is NOW in the document after edit
        try {
          const postEditContent = spanNonNull.rangeIds.map((nodeId: string) => {
            try {
              const location = doc.yxml ? 
                require('../../services/doc/internal/yDocHighLevelAPI').DocumentUtils.findNodeById(doc.yxml, nodeId) : 
                null;
              
              if (location) {
                // 🚨 CRITICAL FIX: DO NOT use location.element.toString() - causes fontFamily contamination!
                // This is in the main AI command used by Claude - contamination here affects all AI edits
                let textContent = '[debug text hidden - toString() disabled to prevent XML contamination]';
                try {
                  const anyElement = location.element as any;
                  if (anyElement._content && typeof anyElement._content === 'string') {
                    textContent = anyElement._content.substring(0, 150) || '[No text content]';
                  }
                } catch {
                  textContent = '[No text content]';
                }
                const changeStatus = location.element.getAttribute('change_status') || 'none';
                return {
                  nodeId,
                  index: location.index,
                  postEditContent: textContent.substring(0, 100),
                  changeStatus,
                  isMarkedAsOld: changeStatus === 'old',
                  isMarkedAsNew: changeStatus === 'new',
                  stillExists: true
                };
              }
              return { nodeId, error: 'node_not_found_after_edit', stillExists: false };
            } catch (error) {
              return { nodeId, error: error instanceof Error ? error.message : 'unknown_error', stillExists: false };
            }
          });
          
          log.log('🔍 [CLAUDE_EDIT_DEBUG] === POST-EDIT CONTENT VERIFICATION ===', {
            postEditNodes: postEditContent,
            nodesStillExist: postEditContent.filter(n => n.stillExists).length,
            nodesMarkedOld: postEditContent.filter(n => n.isMarkedAsOld).length,
            nodesMarkedNew: postEditContent.filter(n => n.isMarkedAsNew).length,
            nodesMissing: postEditContent.filter(n => !n.stillExists).length,
            timestamp: new Date().toISOString()
          });
          
          // 🚨 CRITICAL DEBUG: Check if any nodes were completely lost
          const lostNodes = postEditContent.filter(n => !n.stillExists);
          if (lostNodes.length > 0) {
            console.error('🚨 [NODE_LOSS_DEBUG] === NODES LOST DURING EDIT ===', {
              lostNodeIds: lostNodes.map(n => n.nodeId),
              lostNodeCount: lostNodes.length,
              totalTargeted: spanNonNull.rangeIds.length,
              lossPercentage: (lostNodes.length / spanNonNull.rangeIds.length) * 100,
              possibleCause: 'suggestion_mode_node_replacement',
              timestamp: new Date().toISOString()
            });
          }
          
        } catch (debugError) {
          console.error('❌ [CLAUDE_EDIT_DEBUG] Failed to verify post-edit content:', debugError);
        }
        
        if (!resp || (resp as any).ok === false) {
          return createErrorResult('Replace failed', 'Failed to replace nodes with Markdown content', 'execution');
        }

        const insertedIds = (resp as any).insertedIds || [];
        const allAfter = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const nodesAfter: any[] = allAfter.nodes || [];
        const newNodes = nodesAfter.filter((n: any) => insertedIds.includes(n.id));
        
        // 🔍 LAYER 1: POST-REPLACE NODE AUDIT - Log ALL nodes with detailed analysis
        log.log(`🔍 [POST_REPLACE_NODE_AUDIT] === ALL NODES IN DOCUMENT AFTER REPLACE ===`, {
          totalNodeCount: nodesAfter.length,
          timestamp: new Date().toISOString()
        });
        
        const nodeDetails = nodesAfter.map((n: any) => ({
          id: n.id,
          type: n.type,
          textLength: n.text?.length || 0,
          isStreaming: n.id?.startsWith('streaming_') || false,
          isEmpty: !n.text || n.text.trim().length === 0,
          textPreview: (n.text || '[empty]').substring(0, 30),
          containsOnlyWhitespace: n.text && n.text.trim().length === 0
        }));
        
        log.log(`📋 [POST_REPLACE_NODE_DETAILS] Node-by-node breakdown:`, {
          nodes: nodeDetails
        });
        
        // 🚨 LAYER 4: STRAY STREAMING NODE DETECTION
        const emptyNodes = nodesAfter.filter((n: any) => !n.text || n.text.trim().length === 0);
        const streamingNodes = nodesAfter.filter((n: any) => n.id?.startsWith('streaming_'));
        
        // 🔍 LAYER 8: FINAL DOCUMENT STATE - Comprehensive node analysis
        const detailedNodeAnalysis = nodesAfter.map((n: any) => ({
          id: n.id,
          type: n.type,
          textLength: n.text?.length || 0,
          isEmpty: !n.text || n.text.trim().length === 0,
          textPreview: (n.text || '').substring(0, 80),
          containsNewlines: /\n/.test(n.text || ''),
          newlineCount: ((n.text || '').match(/\n/g) || []).length,
          charCodesStart: (n.text || '').substring(0, 20).split('').map((c: string) => c.charCodeAt(0))
        }));
        
        // 🚨 AGGRESSIVE DEBUG: Print EVERY SINGLE NODE
        log.log('🚨 [DEBUG_ALL_NODES] === PRINTING ALL NODES IN DOCUMENT ===');
        nodesAfter.forEach((n: any, i: number) => {
          log.log(`🚨 [NODE_${i}]`, {
            id: n.id,
            type: n.type,
            text: n.text || '[EMPTY]',
            textLength: n.text?.length || 0,
            isStreaming: n.id?.includes('streaming'),
            isNew: n.change_status === 'new',
            changeStatus: n.change_status,
            revisionId: n.revision_id
          });
        });
        
        log.log(`🔍 [LAYER8_FINAL_DOCUMENT_STATE] === DETAILED NODE ANALYSIS ===`, {
          totalNodes: nodesAfter.length,
          nodeDetails: detailedNodeAnalysis,
          timestamp: new Date().toISOString()
        });
        
        console.error(`🚨 [POST_REPLACE_SUMMARY] === WHITESPACE ANALYSIS ===`, {
          totalNodes: nodesAfter.length,
          emptyNodeCount: emptyNodes.length,
          emptyNodeIds: emptyNodes.map((n: any) => n.id),
          strayStreamingNodeCount: streamingNodes.length,
          strayStreamingNodeIds: streamingNodes.map((n: any) => n.id),
          timestamp: new Date().toISOString()
        });
        
        if (streamingNodes.length > 0) {
          console.error(`🚨 [STRAY_STREAMING_NODES_ALERT] === FOUND ${streamingNodes.length} STRAY PLACEHOLDER NODES ===`, {
            count: streamingNodes.length,
            nodeDetails: streamingNodes.map((n: any) => ({
              id: n.id,
              type: n.type,
              textLength: n.text?.length || 0,
              isEmptyContent: !n.text || n.text.trim().length === 0,
              textPreview: (n.text || '[no content]').substring(0, 50)
            })),
            rootCauseHypothesis: 'Streaming placeholder not deleted by finalization or cleanup'
          });
        }
        
        // 🚨 CRITICAL: Check for STREAMING/PLACEHOLDER nodes that might create gaps
        const streamingOrPlaceholderNodes = nodesAfter.filter((n: any) => 
          n.id?.includes('streaming') || 
          n.id?.includes('placeholder') ||
          (n.text && n.text.includes('['))  // Placeholder markers
        );
        
        if (streamingOrPlaceholderNodes.length > 0) {
          console.error(`🚨 [FOUND_STREAMING_NODES] Found ${streamingOrPlaceholderNodes.length} streaming/placeholder nodes:`, {
            nodes: streamingOrPlaceholderNodes.map((n: any) => ({
              id: n.id,
              type: n.type,
              text: (n.text || '').substring(0, 50),
              textLength: n.text?.length || 0
            }))
          });
        }
        
        if (emptyNodes.length > 0) {
          console.warn(`⚠️ [EMPTY_NODES_ALERT] === FOUND ${emptyNodes.length} EMPTY PARAGRAPH NODES ===`, {
            count: emptyNodes.length,
            nodeDetails: emptyNodes.map((n: any) => ({
              id: n.id,
              type: n.type,
              isStreaming: n.id?.startsWith('streaming_'),
              reason: 'This node will appear as blank space in document'
            })),
            possibleCauses: ['Markdown with only newlines', 'Streaming placeholder not deleted', 'Empty token conversion']
          });
        }
        
        // 🚨 CRITICAL DEBUG: Track document structure changes
        log.log('📊 [DOCUMENT_STRUCTURE_DEBUG] === DOCUMENT STRUCTURE AFTER EDIT ===', {
          operation: 'replace',
          documentId: id,
          nodeCountBefore: originalStructureInfo?.targetNodeCount || 'unknown',
          nodeCountAfter: nodesAfter.length,
          nodeCountChange: nodesAfter.length - (originalStructureInfo?.targetNodeCount || 0),
          insertedNodeIds: insertedIds,
          insertedCount: insertedIds.length,
          targetedForReplacement: spanNonNull.rangeIds,
          targetedCount: spanNonNull.rangeIds.length,
          suggestionMode: replacementOptions.suggestionMode,
          timestamp: new Date().toISOString()
        });
        
        // 🚨 CRITICAL DEBUG: Check for node count discrepancies
        const expectedNodeChange = insertedIds.length - spanNonNull.rangeIds.length;
        const actualNodeChange = nodesAfter.length - (originalStructureInfo?.targetNodeCount || 0);
        
        if (Math.abs(expectedNodeChange - actualNodeChange) > 0) {
          console.error('🚨 [NODE_COUNT_MISMATCH] === UNEXPECTED NODE COUNT CHANGE ===', {
            expectedChange: expectedNodeChange,
            actualChange: actualNodeChange,
            discrepancy: actualNodeChange - expectedNodeChange,
            possibleCause: 'node_loss_or_duplication',
            documentId: id,
            targetedNodes: spanNonNull.rangeIds,
            insertedNodes: insertedIds,
            timestamp: new Date().toISOString()
          });
        }
        
        // 🚨 INFINITE LOOP DEBUG: Track document edit impact for stability analysis
        log.log('🔍 [INFINITE_LOOP_DEBUG] === DOCUMENT EDIT IMPACT ANALYSIS ===', {
          documentId: id,
          operationType: 'doc_write',
          editDetails: {
            targetNodeCount: spanNonNull.rangeIds.length,
            insertedNodeCount: insertedIds.length,
            netNodeChange: actualNodeChange,
            fromNode: fromNode ? fromNode.id : 'document_start',
            toNode: toNode ? toNode.id : 'document_end'
          },
          documentStructure: {
            totalNodesAfter: nodesAfter.length,
            totalNodesBefore: originalStructureInfo?.targetNodeCount || 0,
            lastFiveNodeIds: nodesAfter.slice(-5).map(n => n.id),
            firstFiveNodeIds: nodesAfter.slice(0, 5).map(n => n.id)
          },
          contentAnalysis: {
            markdownLength: markdownContent?.length || 0,
            paragraphCount: (markdownContent?.match(/^(?!#)/gm) || []).filter((line: string) => line.trim()).length,
            headingCount: (markdownContent?.match(/^#+/gm) || []).length
          },
          timestamp: new Date().toISOString(),
          warning: actualNodeChange !== 0 ? 'Document structure modified - may affect future targeting' : 'Structure stable'
        });

        // Bubble context: two nodes above first inserted, inserted block(s), two below last inserted
        let bubbleText = '';
        let bubbleFromId: string | undefined;
        let bubbleToId: string | undefined;
        let bubbleNodeIds: string[] = [];
        let bubbleStartIndex: number | undefined;
        let bubbleEndIndex: number | undefined;
        let editedStartIndex: number | undefined;
        let editedEndIndex: number | undefined;
        let bubbleNodesDetailed: Array<{ id: string; text: string; index: number }> = [];
        if (insertedIds.length > 0 && nodesAfter.length > 0) {
          const indices = insertedIds
            .map((id: string) => nodesAfter.findIndex((n: any) => n.id === id))
            .filter((i: number) => i >= 0)
            .sort((a: number, b: number) => a - b);
          if (indices.length > 0) {
            editedStartIndex = indices[0];
            editedEndIndex = indices[indices.length - 1];
            const minIdx = Math.max(0, (editedStartIndex ?? 0) - 2);
            const maxIdx = Math.min(nodesAfter.length - 1, (editedEndIndex ?? 0) + 2);
            bubbleStartIndex = minIdx;
            bubbleEndIndex = maxIdx;
            const bubbleNodes = nodesAfter.slice(minIdx, maxIdx + 1);
            bubbleNodeIds = bubbleNodes.map((n: any) => n.id);
            bubbleFromId = bubbleNodes[0]?.id;
            bubbleToId = bubbleNodes[bubbleNodes.length - 1]?.id;
            bubbleText = bubbleNodes
              .map((n: any) => (typeof n.text === 'string' ? n.text.trim() : ''))
              .filter(Boolean)
              .join('\n');
            bubbleNodesDetailed = bubbleNodes.map((n: any, offset: number) => ({ id: n.id, text: (typeof n.text === 'string' ? n.text.trim() : ''), index: minIdx + offset }));
          }
        }

        traceService.log({
          traceId: effectiveTraceId,
          type: 'tool_success',
          data: { tool: 'doc_write', op: 'replace', replacedCount: spanNonNull.rangeIds.length }
        });
        // ✅ ENHANCED: Generate contextual completion messages
        const contextualMessage = generateContextualMessage({
          operation: 'replace',
          nodeCount: spanNonNull.rangeIds.length,
          markdownContent: enhancedMarkdown,
          originalStructureInfo,
          bubbleText,
          newNodes
        });

        const success = createSuccessResult(
          contextualMessage,
          {
            type: 'document_replace',
            replacedCount: spanNonNull.rangeIds.length,
            newNodes,
            revisionId: (resp as any).revisionId,
            replacedNodeIds: spanNonNull.rangeIds,
            insertedNodeIds: insertedIds,
            contextWindow: bubbleText ? {
              fromId: bubbleFromId,
              toId: bubbleToId,
              nodeIds: bubbleNodeIds,
              text: bubbleText,
              nodes: bubbleNodesDetailed,
              indices: {
                bubbleStartIndex,
                bubbleEndIndex,
                editedStartIndex,
                editedEndIndex
              }
            } : undefined,
            // ✅ NEW: Enhanced message context for better UI display
            messageContext: {
              fullContent: enhancedMarkdown, // ← Save full content for UI display
              contentPreview: enhancedMarkdown.substring(0, 100) + (enhancedMarkdown.length > 100 ? '...' : ''),
              structureType: originalStructureInfo?.hasLists ? 'list' : 'paragraph',
              operationSummary: `Updated ${spanNonNull.rangeIds.length} section${spanNonNull.rangeIds.length > 1 ? 's' : ''}`,
              beforeContext: bubbleText?.substring(0, 50).trim() + '...',
              afterPreview: newNodes.map((n: any) => n.text?.trim?.().substring(0, 50)).filter(Boolean).join(' | ')
            }
          },
          { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: 'markdown_replace' } as any }
        );
        
        // 🚀 NEW: Refresh document context after successful AI edit
        try {
          log.log(`🔄 [TESTING] AI_EDIT_CONTEXT_REFRESH_START:`, {
            operation: 'replace',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            replacedNodeCount: spanNonNull.rangeIds.length,
            newContentLength: enhancedMarkdown.length,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_start'
          });
          
          const { useContextStore } = await import('../../../../components/Space/SpaceChat/store/contextStore');
          const { notifyContentUpdate } = useContextStore.getState();
          notifyContentUpdate(id, {  // Use llmId (params.id) instead of windowId
            contentLength: enhancedMarkdown.length,
            source: 'ai_doc_write_replace'
          });
          
          // 🚨 CRITICAL: Update fresh content for next agentic iteration (prevents stale node IDs)
          if (hasDocumentReferences && backendMustFetchFresh) {
            log.log('🔄 [FRESH_CONTENT_UPDATE] Updating fresh content after successful replace:', {
              documentId: id,
              oldNodeCount: spanNonNull.rangeIds.length,
              newNodeCount: insertedIds.length,
              reason: 'node_structure_changed_after_replace'
            });
            
            // Store a flag that fresh content needs to be re-fetched for this document
            (context as any).documentContentStale = true;
            (context as any).staleDocumentId = id;
          }
          
          log.log(`✅ [TESTING] AI_EDIT_CONTEXT_REFRESH_SUCCESS:`, {
            operation: 'replace',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            contextRefreshed: true,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_success'
          });
          
          // 🚀 UPDATE ENTITYSTORE: Refresh metadata immediately to prevent stale context
          try {
            const { docsCRDTService } = await import('../../services/crdt/docsCRDTService');
            const metadata = docsCRDTService.calculateDocumentMetadata(doc.docId);
            
            if (metadata) {
              log.log(`📊 [doc_write] Updating EntityStore metadata after replace:`, {
                operation: 'replace',
                llmId: id,
                docId: doc.docId,
                windowId: doc.windowId,
                newMetadata: metadata
              });
              
              const useEntityStore = (await import('../../../../stores/entityStore')).default;
              const { entities, updateEntity, addEntity } = useEntityStore.getState();
              
              const entity = entities.find((e: any) => (e.llmId || e._id) === id || e._id === doc.docId);
              
              if (entity) {
                updateEntity({
                  ...entity,
                  skeleton: {
                    ...entity.skeleton,
                    metadata: {
                      ...metadata,
                      lastUpdated: new Date().toISOString()
                    }
                  }
                });
                log.log(`✅ [doc_write] EntityStore metadata updated for ${id}:`, metadata);
              } else {
                // 🚀 FALLBACK: Entity not in EntityStore - fetch and add it
                console.warn(`⚠️ [doc_write] Entity not found in EntityStore, fetching from backend: ${id}`);
                
                try {
                  const { entityService } = await import('../../../../services/entityService');
                  
                  let fetchedEntity: any = null;
                  try {
                    fetchedEntity = await entityService.get(id);
                  } catch (llmIdError) {
                    log.log(`[doc_write] Failed to fetch by llmId, trying docId: ${doc.docId}`);
                    try {
                      fetchedEntity = await entityService.get(doc.docId);
                    } catch (docIdError) {
                      console.error(`[doc_write] Failed to fetch by both llmId and docId`);
                    }
                  }
                  
                  if (fetchedEntity) {
                    const entityWithMetadata = {
                      ...fetchedEntity,
                      skeleton: {
                        ...fetchedEntity.skeleton,
                        metadata: {
                          ...metadata,
                          lastUpdated: new Date().toISOString()
                        }
                      }
                    };
                    
                    addEntity(entityWithMetadata);
                    
                    log.log(`✅ [doc_write] Fetched and added entity to EntityStore with metadata:`, {
                      entityId: fetchedEntity._id,
                      llmId: fetchedEntity.llmId,
                      name: fetchedEntity.name,
                      metadata
                    });
                  } else {
                    console.error(`❌ [doc_write] Could not fetch entity from backend: ${id}`);
                  }
                } catch (fetchError) {
                  console.error(`❌ [doc_write] Failed to fetch and add entity:`, fetchError);
                }
              }
            } else {
              console.warn(`⚠️ [doc_write] Failed to calculate metadata for ${doc.docId}`);
            }
          } catch (metadataError) {
            console.error(`❌ [doc_write] Failed to update EntityStore metadata:`, metadataError);
            // Don't throw - this is a non-critical optimization
          }
          
        } catch (error) {
          console.error(`❌ [TESTING] AI_EDIT_CONTEXT_REFRESH_FAILED:`, {
            operation: 'replace',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            keyUsedForRefresh: id,
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_failed'
          });
        }
        
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...', text_total: bubbleText?.slice(0, 200) }); } catch {}
        // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
        await forceFlushDocumentSave(doc.docId);
        return success;
      }

      // 2) REMOVE (JSON)
      if (wantsRemove) {
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Computing remove span...' }); } catch {}
        
        log.log('🔍 [DOC_WRITE_REMOVE_DEBUG] === REMOVE OPERATION START ===', {
          timestamp: new Date().toISOString(),
          removeNodes: removeNodes,
          fromNode,
          toNode,
          anchors,
          useNonContiguous: !!removeNodes && Array.isArray(removeNodes) && removeNodes.length > 0
        });
        
        // ✅ Use contiguous parameter with smart defaults
        const useContiguous = params.contiguous !== undefined ? params.contiguous : false; // Default false for remove
        
        log.log('🔍 [CONTIGUOUS_DEBUG] === CONTIGUOUS MODE SELECTION ===', {
          operation: 'remove',
          explicitContiguous: params.contiguous,
          defaultContiguous: false,
          finalContiguous: useContiguous
        });
        
        // ✅ FIX: Use parameter-driven contiguous mode
        const span = selectAll
          ? resolveAllSpan()
          : resolveSpan(removeNodes, fromNode, toNode, anchors, {
              contiguous: useContiguous
            });
        
        log.log('🔍 [DOC_WRITE_REMOVE_DEBUG] === SPAN RESOLUTION RESULT ===', {
          timestamp: new Date().toISOString(),
          requestedNodes: removeNodes,
          resolvedSpan: span,
          resolvedNodeCount: span?.rangeIds?.length || 0,
          resolvedNodes: span?.rangeIds || [],
          expansionPrevented: true
        });
        
        if (!span) {
          if (selectAll) {
            return createErrorResult('Document is empty', 'Cannot remove: the document has no content.', 'validation');
          }
          const suggestion = `For scattered removal, try contiguous: false. For section removal, use contiguous: true. Current: contiguous=${useContiguous}`;
          return createErrorResult('Invalid remove target', `Provide explicit removeNodes or fromNode+toNode to define range for removal. ${suggestion}`, 'validation');
        }

        traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_write', op: 'remove', targetCount: span.rangeIds.length } });
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Marking nodes for removal...' }); } catch {}
        const resp = removeJson(
          { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any,
          { ids: span.rangeIds },
          { batchDescription: 'Remove', suggestionMode: useSuggestionMode }
        );
        if (!resp || (resp as any).ok === false) {
          return createErrorResult('Remove failed', 'Failed to mark nodes for removal', 'execution');
        }

        // Build context window around the removed span: two above, two below
        const allAfter = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const nodesAfter: any[] = allAfter.nodes || [];
        let bubbleFromId: string | undefined;
        let bubbleToId: string | undefined;
        let bubbleNodeIds: string[] = [];
        let bubbleText = '';
        let bubbleStartIndex: number | undefined;
        let bubbleEndIndex: number | undefined;
        let editedStartIndex: number | undefined;
        let editedEndIndex: number | undefined;
        let bubbleNodesDetailed: Array<{ id: string; text: string; index: number }> = [];
        if (nodesAfter.length > 0) {
          // Estimate the position near where the first removed id was; pick the closest surviving index
          const firstTargetId = span.rangeIds[0];
          let anchorIndex = nodesAfter.findIndex((n: any) => n.id === firstTargetId);
          if (anchorIndex < 0) {
            // fallback: try to anchor by proximity to fromId/toId textual neighborhood by scanning
            anchorIndex = nodesAfter.findIndex((n: any) => typeof n.text === 'string' && n.text.trim().length > 0);
            if (anchorIndex < 0) anchorIndex = 0;
          }
          editedStartIndex = Math.max(0, anchorIndex);
          editedEndIndex = Math.max(0, Math.min(nodesAfter.length - 1, anchorIndex));
          const minIdx = Math.max(0, editedStartIndex - 2);
          const maxIdx = Math.min(nodesAfter.length - 1, editedEndIndex + 2);
          bubbleStartIndex = minIdx;
          bubbleEndIndex = maxIdx;
          const bubbleNodes = nodesAfter.slice(minIdx, maxIdx + 1);
          bubbleNodeIds = bubbleNodes.map((n: any) => n.id);
          bubbleFromId = bubbleNodes[0]?.id;
          bubbleToId = bubbleNodes[bubbleNodes.length - 1]?.id;
          bubbleText = bubbleNodes
            .map((n: any) => (typeof n.text === 'string' ? n.text.trim() : ''))
            .filter(Boolean)
            .join('\n');
          bubbleNodesDetailed = bubbleNodes.map((n: any, offset: number) => ({ id: n.id, text: (typeof n.text === 'string' ? n.text.trim() : ''), index: minIdx + offset }));
        }

        traceService.log({
          traceId: effectiveTraceId,
          type: 'tool_success',
          data: { tool: 'doc_write', op: 'remove', removedCount: span.rangeIds.length }
        });
        // ✅ ENHANCED: Generate contextual completion message for remove operation
        const contextualMessage = generateContextualMessage({
          operation: 'remove',
          nodeCount: span.rangeIds.length,
          bubbleText
        });

        const success = createSuccessResult(
          contextualMessage,
          {
            type: 'document_remove',
            removedCount: span.rangeIds.length,
            removedNodeIds: span.rangeIds,
            contextWindow: bubbleText ? {
              fromId: bubbleFromId,
              toId: bubbleToId,
              nodeIds: bubbleNodeIds,
              text: bubbleText,
              nodes: bubbleNodesDetailed,
              indices: {
                bubbleStartIndex,
                bubbleEndIndex,
                editedStartIndex,
                editedEndIndex
              }
            } : undefined,
            // ✅ NEW: Enhanced message context for remove operations
            messageContext: {
              operationSummary: `Removed ${span.rangeIds.length} section${span.rangeIds.length > 1 ? 's' : ''}`,
              beforeContext: bubbleText?.substring(0, 100) + '...'
            }
          },
          { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: 'remove' } as any }
        );
        
        // 🚀 NEW: Refresh document context after successful AI edit
        try {
          log.log(`🔄 [TESTING] AI_EDIT_CONTEXT_REFRESH_START:`, {
            operation: 'remove',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            removedNodeCount: span.rangeIds.length,
            removedNodeIds: span.rangeIds,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_start'
          });
          
          const { useContextStore } = await import('../../../../components/Space/SpaceChat/store/contextStore');
          const { notifyContentUpdate } = useContextStore.getState();
          notifyContentUpdate(id, {  // Use llmId (params.id) instead of windowId
            source: 'ai_doc_write_remove'
          });
          
          log.log(`✅ [TESTING] AI_EDIT_CONTEXT_REFRESH_SUCCESS:`, {
            operation: 'remove',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            contextRefreshed: true,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_success'
          });
          
          // 🚀 UPDATE ENTITYSTORE: Refresh metadata immediately to prevent stale context
          try {
            const { docsCRDTService } = await import('../../services/crdt/docsCRDTService');
            const metadata = docsCRDTService.calculateDocumentMetadata(doc.docId);
            
            if (metadata) {
              log.log(`📊 [doc_write] Updating EntityStore metadata after remove:`, {
                operation: 'remove',
                llmId: id,
                docId: doc.docId,
                windowId: doc.windowId,
                newMetadata: metadata
              });
              
              const useEntityStore = (await import('../../../../stores/entityStore')).default;
              const { entities, updateEntity, addEntity } = useEntityStore.getState();
              
              const entity = entities.find((e: any) => (e.llmId || e._id) === id || e._id === doc.docId);
              
              if (entity) {
                updateEntity({
                  ...entity,
                  skeleton: {
                    ...entity.skeleton,
                    metadata: {
                      ...metadata,
                      lastUpdated: new Date().toISOString()
                    }
                  }
                });
                log.log(`✅ [doc_write] EntityStore metadata updated for ${id}:`, metadata);
              } else {
                // 🚀 FALLBACK: Entity not in EntityStore - fetch and add it
                console.warn(`⚠️ [doc_write] Entity not found in EntityStore, fetching from backend: ${id}`);
                
                try {
                  const { entityService } = await import('../../../../services/entityService');
                  
                  let fetchedEntity: any = null;
                  try {
                    fetchedEntity = await entityService.get(id);
                  } catch (llmIdError) {
                    log.log(`[doc_write] Failed to fetch by llmId, trying docId: ${doc.docId}`);
                    try {
                      fetchedEntity = await entityService.get(doc.docId);
                    } catch (docIdError) {
                      console.error(`[doc_write] Failed to fetch by both llmId and docId`);
                    }
                  }
                  
                  if (fetchedEntity) {
                    const entityWithMetadata = {
                      ...fetchedEntity,
                      skeleton: {
                        ...fetchedEntity.skeleton,
                        metadata: {
                          ...metadata,
                          lastUpdated: new Date().toISOString()
                        }
                      }
                    };
                    
                    addEntity(entityWithMetadata);
                    
                    log.log(`✅ [doc_write] Fetched and added entity to EntityStore with metadata:`, {
                      entityId: fetchedEntity._id,
                      llmId: fetchedEntity.llmId,
                      name: fetchedEntity.name,
                      metadata
                    });
                  } else {
                    console.error(`❌ [doc_write] Could not fetch entity from backend: ${id}`);
                  }
                } catch (fetchError) {
                  console.error(`❌ [doc_write] Failed to fetch and add entity:`, fetchError);
                }
              }
            } else {
              console.warn(`⚠️ [doc_write] Failed to calculate metadata for ${doc.docId}`);
            }
          } catch (metadataError) {
            console.error(`❌ [doc_write] Failed to update EntityStore metadata:`, metadataError);
            // Don't throw - this is a non-critical optimization
          }
          
        } catch (error) {
          console.error(`❌ [TESTING] AI_EDIT_CONTEXT_REFRESH_FAILED:`, {
            operation: 'remove',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            keyUsedForRefresh: id,
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_failed'
          });
        }
        
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...', text_total: bubbleText?.slice(0, 200) }); } catch {}
        // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
        await forceFlushDocumentSave(doc.docId);
        return success;
      }

      // 3) INSERT (Markdown-only via proper Insert command with page-aware logic)
      if (wantsInsert) {
        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === INSERT OPERATION START ===', {
          timestamp: new Date().toISOString(),
          toolUseId: context.executionId,
          documentId: id,
          insertLocation: insertLocation,
          markdownContentLength: markdownContent?.length || 0,
          markdownContentPreview: markdownContent?.substring(0, 100) + '...'
        });

        try { context.onProgress?.({ type: 'streaming_update', activity: 'Preparing insertion...' }); } catch {}
        
        // ✅ NEW: Remove any streaming placeholder to prevent double-writes
        try {
          const yxmlArray = doc.yxml.toArray();
          const placeholderIndex = yxmlArray.findIndex((el: any) => {
            if (typeof el.getAttribute === 'function') {
              const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
              const isFailed = el.getAttribute('data-streaming-failed') === 'true';
              return isActiveStreaming || isFailed;
            }
            return false;
          });
          
          if (placeholderIndex !== -1) {
            const placeholderEl = yxmlArray[placeholderIndex] as any;
            const placeholderId = placeholderEl && typeof placeholderEl.getAttribute === 'function' 
              ? placeholderEl.getAttribute('id') 
              : 'unknown';
            
            log.log(`🧹 [INSERT_CLEANUP] Found streaming placeholder at index ${placeholderIndex}, removing`, {
              yxmlLength: yxmlArray.length,
              placeholderId
            });
            
            doc.yDoc.transact(() => {
              doc.yxml.delete(placeholderIndex, 1);
            }, { source: 'cleanup-streaming-placeholder' });
            
            // Verify deletion
            const yxmlArrayAfter = doc.yxml.toArray();
            const stillExists = yxmlArrayAfter.some((el: any) => {
              if (typeof el.getAttribute === 'function') {
                const isActiveStreaming = el.getAttribute('data-streaming') === 'true';
                const isFailed = el.getAttribute('data-streaming-failed') === 'true';
                return isActiveStreaming || isFailed;
              }
              return false;
            });
            
            if (stillExists) {
              console.error(`❌ [INSERT_CLEANUP_ERROR] Placeholder still exists after deletion!`);
              throw new Error('Placeholder deletion verification failed');
            }
            
            log.log(`✅ [INSERT_CLEANUP] Placeholder deleted successfully`, {
              yxmlLengthBefore: yxmlArray.length,
              yxmlLengthAfter: yxmlArrayAfter.length
            });
          } else {
            log.log(`ℹ️ [INSERT_CLEANUP] No streaming placeholder found`, {
              yxmlLength: yxmlArray.length
            });
          }
        } catch (cleanupError) {
          console.error(`❌ [INSERT_CLEANUP_ERROR] Error cleaning placeholder:`, {
            error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
            stack: cleanupError instanceof Error ? cleanupError.stack : undefined
          });
          throw cleanupError;  // ✅ FAIL if cleanup fails
        }
        
        // Pre-fetch any grok: or unsplash: image URLs before the markdown pipeline runs
        const resolvedMarkdown = await preFetchImagesInMarkdown(String(markdownContent));

        // ✅ NEW: Handle position-based insertions (at: 'start' | 'end') for empty documents
        if (insertLocation && (insertLocation as any).at) {
          const atLocation = (insertLocation as any).at;
          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] POSITION-BASED INSERT DETECTED', {
            atLocation,
            isStart: atLocation === 'start',
            isEnd: atLocation === 'end'
          });

          // Convert markdown up-front
          const contentArray = markdownToContentArray(resolvedMarkdown);
          const docCtx = { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any;
          
          // 🆕 STREAMING: Show preview of content before insertion
          try {
            const progressData = {
              type: 'live_preview',
              message: `Inserting at ${atLocation}...`,
              liveMarkdown: markdownContent,
              position: `document ${atLocation}`,
              isStreaming: true
            };
            
            log.log('[doc_write] 🎬 Emitting live preview (position-based):', {
              markdownLength: markdownContent.length,
              position: progressData.position,
              atLocation
            });
            
            context.onProgress?.(progressData);
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            console.error('[doc_write] ❌ Preview emission failed:', err);
          }

          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === CALLING insertViaFacade (POSITION-BASED) ===', {
            timestamp: new Date().toISOString(),
            location: insertLocation,
            contentArray: contentArray.map(c => ({ type: c.type, textLength: hasText(c) ? c.text.length : 0 }))
          });

          // ✅ PRE-INSERT VALIDATION: Log document state before attempting insert
          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] PRE-INSERT VALIDATION', {
            timestamp: new Date().toISOString(),
            hasDoc: !!doc,
            hasYxml: !!doc?.yxml,
            hasYDoc: !!doc?.yDoc,
            yxmlLength: doc?.yxml?.length,
            documentId: doc?.docId,
            windowId: doc?.windowId,
            contentArrayLength: contentArray.length,
            contentTypes: contentArray.map(c => c.type),
            insertLocation,
            atLocation,
            revisionId,
            useSuggestionMode
          });

          const resp = insertViaFacade(
            docCtx,
            contentArray,
            insertLocation as any,
            {
              suggestionMode: useSuggestionMode,
              revisionId,
              batchDescription: `Insert (${atLocation})`
            }
          );

          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === POSITION-BASED insertViaFacade RESPONSE ===', {
            timestamp: new Date().toISOString(),
            hasResponse: !!resp,
            responseOk: resp?.ok,
            insertedNodesCount: resp?.insertedNodes?.length || 0,
            responseData: resp
          });

          if (!resp || !resp.ok) {
            const errorDetail = (resp as any)?.error || (resp as any)?.message || 'Unknown error from insertViaFacade';
            console.error('❌ [DOC_WRITE_INSERT_DEBUG] POSITION-BASED INSERT FAILED', {
              timestamp: new Date().toISOString(),
              response: resp,
              error: errorDetail,
              errorType: typeof errorDetail,
              insertLocation,
              atLocation,
              contentLength: markdownContent?.length,
              contentPreview: markdownContent?.substring(0, 200),
              contentArrayLength: contentArray.length,
              documentId: id,
              hasDoc: !!doc,
              hasYxml: !!doc?.yxml,
              yxmlLength: doc?.yxml?.length
            });
            return createErrorResult(
              'Insert operation failed',
              `Failed to insert at ${atLocation}: ${errorDetail}. Check console for details.`,
              'execution'
            );
          }

          const insertedNodes = resp.insertedNodes || [];
          
          // ✅ ENHANCED: Generate contextual completion message
          const contextualMessage = generateContextualMessage({
            operation: 'insert',
            nodeCount: insertedNodes.length,
            markdownContent,
            newNodes: insertedNodes
          });
          
          const success = createSuccessResult(
            `${contextualMessage} (at document ${atLocation})`,
            {
              type: 'document_insert',
              insertedCount: insertedNodes.length,
              insertedNodeIds: insertedNodes.map((n: any) => n.id),
              newNodes: insertedNodes,
              // ✅ Enhanced message context for position-based insert operations
              messageContext: {
                fullContent: markdownContent, // ← Save full content for UI display
                contentPreview: markdownContent.substring(0, 100) + (markdownContent.length > 100 ? '...' : ''),
                operationSummary: contextualMessage,
                insertLocation: atLocation,
                insertedCount: insertedNodes.length
              }
            },
            { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: `insert_markdown_${atLocation}` } as any }
          );

          traceLog({ traceId, type: 'tool_success', data: { tool: 'doc_write', op: `insert_markdown_${atLocation}`, insertedCount: insertedNodes.length } });
          // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
          await forceFlushDocumentSave(doc.docId);
          return success;
        }

        // ✅ EXISTING: Handle anchor-based insertions (before/after)
        if (!insertLocation || (!insertLocation.before && !insertLocation.after)) {
          log.log('❌ [DOC_WRITE_INSERT_DEBUG] INSERT VALIDATION FAILED: Missing insert anchor', {
            insertLocation,
            hasBefore: !!insertLocation?.before,
            hasAfter: !!insertLocation?.after
          });
          return createErrorResult('Missing insert anchor', 'Provide insertLocation with before or after to specify where to insert', 'validation');
        }

        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] INSERT VALIDATION PASSED, proceeding with document read', {
          insertLocation,
          anchorType: insertLocation.before ? 'before' : 'after',
          anchorValue: insertLocation.before || insertLocation.after
        });

        const loc: InsertLocation = insertLocation;
        const all = readAllDoc({ yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any);
        const allNodes: any[] = all.nodes || [];
        
        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] DOCUMENT READ COMPLETE', {
          timestamp: new Date().toISOString(),
          totalNodesFound: allNodes.length,
          nodeIds: allNodes.map((n: any) => n.id),
          nodeTypes: allNodes.map((n: any) => ({ id: n.id, type: n.type }))
        });
        
        if (!allNodes.length) {
          log.log('❌ [DOC_WRITE_INSERT_DEBUG] EMPTY DOCUMENT ERROR');
          return createErrorResult('Empty document', 'Cannot insert into an empty document without an anchor', 'validation');
        }

        const targetId = String((loc as any).before || (loc as any).after);
        const targetIndex = allNodes.findIndex((n: any) => n.id === targetId);

        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] ANCHOR RESOLUTION', {
          timestamp: new Date().toISOString(),
          targetId,
          targetIndex,
          anchorFound: targetIndex !== -1,
          targetNode: targetIndex !== -1 ? allNodes[targetIndex] : null
        });

        // ✅ Convert markdown up-front for both primary and fallback paths
        const contentArray = markdownToContentArray(resolvedMarkdown);
        const docCtx = { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any;
        
        // 🆕 STREAMING: Emit preview of content to be inserted
        try {
          const progressData = {
            type: 'live_preview',
            message: 'Preparing to insert...',
            liveMarkdown: markdownContent,
            position: (loc as any).before ? `before ${targetId}` : 
                      (loc as any).after ? `after ${targetId}` : 'document',
            isStreaming: true
          };
          
          log.log('[doc_write] 🎬 Emitting live preview:', {
            markdownLength: markdownContent.length,
            position: progressData.position,
            hasCallback: !!context.onProgress
          });
          
          context.onProgress?.(progressData);
          
          // Delay to let UI show the preview before committing
          log.log('[doc_write] ⏸️ Waiting 500ms for preview display...');
          await new Promise(resolve => setTimeout(resolve, 500));
          log.log('[doc_write] ✅ Preview delay complete, committing to document');
        } catch (err) {
          console.error('[doc_write] ❌ Preview emission failed:', err);
        }

        if (targetIndex === -1) {
          // ⚠️ Anchor missing — gracefully fall back to appending at end
          const fallbackLoc: InsertLocation = { at: 'end' };
          console.warn('[doc_write] Invalid insert target anchor not found; falling back to { at: "end" }', {
            requestedLocation: loc,
            fallbackLoc
          });

          traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_write', op: 'insert_markdown_fallback_end', location: fallbackLoc } });
          try { context.onProgress?.({ type: 'streaming_update', activity: 'Anchor missing — appending at end...' }); } catch {}

          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === FALLBACK: CALLING insertViaFacade (END) ===', {
            timestamp: new Date().toISOString(),
            fallbackLocation: fallbackLoc,
            contentArray: contentArray.map(c => ({ type: c.type, textLength: hasText(c) ? c.text.length : 0 }))
          });

          const resp = insertViaFacade(
            docCtx,
            contentArray,
            fallbackLoc as any,
            {
              suggestionMode: useSuggestionMode,
              revisionId,
              batchDescription: `Insert (fallback to end)`
            }
          );

          log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === FALLBACK insertViaFacade RESPONSE ===', {
            timestamp: new Date().toISOString(),
            hasResponse: !!resp,
            responseOk: resp?.ok,
            insertedNodesCount: resp?.insertedNodes?.length || 0,
            responseData: resp
          });

          if (!resp || !resp.ok) {
            log.log('❌ [DOC_WRITE_INSERT_DEBUG] FALLBACK INSERT FAILED', {
              timestamp: new Date().toISOString(),
              response: resp,
              responseOk: resp?.ok
            });
            return createErrorResult('Insert failed', 'Failed to insert markdown content using Insert command (fallback end)', 'execution');
          }

          const insertedNodes = resp.insertedNodes || [];
          log.log('🔧 [PAGE_STRUCTURE_FIX] === FALLBACK INSERT (END) SUCCESS ===', {
            insertedCount: insertedNodes.length,
            insertedNodeIds: insertedNodes.map((n: any) => n.id),
            revisionId: (resp as any).revisionId,
            insertionMethod: 'fallback_explicit_end_insert'
          });

          // ✅ ENHANCED: Generate contextual completion message for fallback insert
          const contextualMessage = generateContextualMessage({
            operation: 'insert',
            nodeCount: insertedNodes.length,
            markdownContent,
            newNodes: insertedNodes
          });

          // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
          await forceFlushDocumentSave(doc.docId);
          return createSuccessResult(
            contextualMessage,
            {
              type: 'document_insert_markdown',
              method: 'hierarchical_insert_command',
              revisionId: (resp as any).revisionId,
              newNodes: insertedNodes,
              insertedNodeIds: insertedNodes.map((n: any) => n.id),
              // ✅ NEW: Enhanced message context for insert operations
              messageContext: {
                fullContent: markdownContent, // ← Save full content for UI display
                contentPreview: markdownContent.substring(0, 100) + (markdownContent.length > 100 ? '...' : ''),
                operationSummary: contextualMessage,
                insertLocation: 'end',
                insertedCount: insertedNodes.length
              }
            },
            { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: 'insert_markdown_fallback_end' } as any }
          );
        }

        // ✅ FIXED: Use proper Insert command instead of document reconstruction
        log.log('🔧 [AI_PAGINATION_FIX] === USING INSERT COMMAND FOR EXPLICIT INSERTION ===', {
          insertLocation: loc,
          targetId,
          contentArray: contentArray.map(c => ({ type: c.type, textLength: hasText(c) ? c.text.length : 0 })),
          insertionStrategy: 'explicit_hierarchical_insert'
        });

        traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_write', op: 'insert_markdown', location: insertLocation } });
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Applying insertion...' }); } catch {}

        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === CALLING insertViaFacade ===', {
          timestamp: new Date().toISOString(),
          docCtx: {
            hasYxml: !!docCtx.yxml,
            hasYDoc: !!docCtx.doc,
            docId: docCtx.docId
          },
          contentArray: contentArray.map(c => ({ type: c.type, textLength: hasText(c) ? c.text.length : 0, textPreview: hasText(c) ? c.text.substring(0, 50) : '' })),
          insertLocation: loc,
          options: {
            suggestionMode: useSuggestionMode,
            revisionId,
            batchDescription: 'Insert'
          }
        });

        // Use the Insert command which has proper page-aware logic
        const resp = insertViaFacade(
          docCtx,
          contentArray,
          loc,
          {
            suggestionMode: useSuggestionMode,
            revisionId,
            batchDescription: 'Insert'
          }
        );
        
        log.log('🔍 [DOC_WRITE_INSERT_DEBUG] === insertViaFacade RESPONSE ===', {
          timestamp: new Date().toISOString(),
          hasResponse: !!resp,
          responseOk: resp?.ok,
          insertedNodesCount: resp?.insertedNodes?.length || 0,
          responseKeys: resp ? Object.keys(resp) : null,
          responseData: resp
        });
        
        if (!resp || !resp.ok) {
          log.log('❌ [DOC_WRITE_INSERT_DEBUG] INSERT FAILED', {
            timestamp: new Date().toISOString(),
            response: resp,
            responseOk: resp?.ok
          });
          return createErrorResult('Insert failed', 'Failed to insert markdown content using Insert command', 'execution');
        }

        const insertedNodes = resp.insertedNodes || [];

        log.log('🔧 [PAGE_STRUCTURE_FIX] === EXPLICIT INSERT SUCCESS ===', {
          insertedCount: insertedNodes.length,
          insertedNodeIds: insertedNodes.map((n: any) => n.id),
          revisionId: (resp as any).revisionId,
          insertionMethod: 'explicit_page_aware_insert'
        });

        traceService.log({
          traceId: effectiveTraceId,
          type: 'tool_success',
          data: { tool: 'doc_write', op: 'insert_markdown' }
        });
        // ✅ ENHANCED: Generate contextual completion message for explicit insert
        const contextualMessage = generateContextualMessage({
          operation: 'insert',
          nodeCount: insertedNodes.length,
          markdownContent,
          newNodes: insertedNodes
        });

        const success = createSuccessResult(
          contextualMessage,
          {
            type: 'document_insert_markdown',
            method: 'hierarchical_insert_command',
            revisionId: (resp as any).revisionId,
            newNodes: insertedNodes,
            insertedNodeIds: insertedNodes.map((n: any) => n.id),
            // ✅ NEW: Enhanced message context for insert operations
            messageContext: {
              fullContent: markdownContent, // ← Save full content for UI display
              contentPreview: markdownContent.substring(0, 100) + (markdownContent.length > 100 ? '...' : ''),
              operationSummary: contextualMessage,
              insertLocation: (loc as any).before ? `before ${(loc as any).before}` : `after ${(loc as any).after}`,
              insertedCount: insertedNodes.length
            }
          },
          { format: 'json', metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId], method: 'insert_markdown' } as any }
        );
        
        // 🚀 NEW: Refresh document context after successful AI edit
        try {
          log.log(`🔄 [TESTING] AI_EDIT_CONTEXT_REFRESH_START:`, {
            operation: 'insert',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            insertedNodeCount: insertedNodes.length,
            newContentLength: markdownContent.length,
            insertLocation: (loc as any).before ? `before ${(loc as any).before}` : `after ${(loc as any).after}`,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_start'
          });
          
          const { useContextStore } = await import('../../../../components/Space/SpaceChat/store/contextStore');
          const { notifyContentUpdate } = useContextStore.getState();
          notifyContentUpdate(id, {  // Use llmId (params.id) instead of windowId
            contentLength: markdownContent.length,
            source: 'ai_doc_write_insert'
          });
          
          // 🚨 CRITICAL: Mark content as stale for next agentic iteration
          if (hasDocumentReferences && backendMustFetchFresh) {
            log.log('🔄 [FRESH_CONTENT_UPDATE] Marking content stale after successful insert:', {
              documentId: id,
              insertedNodeCount: insertedNodes.length,
              reason: 'node_structure_changed_after_insert'
            });
            
            (context as any).documentContentStale = true;
            (context as any).staleDocumentId = id;
          }
          
          log.log(`✅ [TESTING] AI_EDIT_CONTEXT_REFRESH_SUCCESS:`, {
            operation: 'insert',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            contextRefreshed: true,
            keyUsedForRefresh: id,
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_success'
          });
          
          // 🚀 UPDATE ENTITYSTORE: Refresh metadata immediately to prevent stale context
          try {
            const { docsCRDTService } = await import('../../services/crdt/docsCRDTService');
            const metadata = docsCRDTService.calculateDocumentMetadata(doc.docId);
            
            if (metadata) {
              log.log(`📊 [doc_write] Updating EntityStore metadata after insert:`, {
                operation: 'insert',
                llmId: id,
                docId: doc.docId,
                windowId: doc.windowId,
                newMetadata: metadata
              });
              
              const useEntityStore = (await import('../../../../stores/entityStore')).default;
              const { entities, updateEntity, addEntity } = useEntityStore.getState();
              
              // Find entity by llmId or _id
              const entity = entities.find((e: any) => (e.llmId || e._id) === id || e._id === doc.docId);
              
              if (entity) {
                updateEntity({
                  ...entity,
                  skeleton: {
                    ...entity.skeleton,
                    metadata: {
                      ...metadata,
                      lastUpdated: new Date().toISOString()
                    }
                  }
                });
                log.log(`✅ [doc_write] EntityStore metadata updated for ${id}:`, metadata);
              } else {
                // 🚀 FALLBACK: Entity not in EntityStore - fetch and add it
                console.warn(`⚠️ [doc_write] Entity not found in EntityStore, fetching from backend: ${id}`);
                
                try {
                  const { entityService } = await import('../../../../services/entityService');
                  
                  // Try to fetch by llmId first (get() automatically resolves llmId), fallback to docId
                  let fetchedEntity: any = null;
                  try {
                    fetchedEntity = await entityService.get(id);
                  } catch (llmIdError) {
                    log.log(`[doc_write] Failed to fetch by llmId, trying docId: ${doc.docId}`);
                    try {
                      fetchedEntity = await entityService.get(doc.docId);
                    } catch (docIdError) {
                      console.error(`[doc_write] Failed to fetch by both llmId and docId`);
                    }
                  }
                  
                  if (fetchedEntity) {
                    // Add metadata to fetched entity
                    const entityWithMetadata = {
                      ...fetchedEntity,
                      skeleton: {
                        ...fetchedEntity.skeleton,
                        metadata: {
                          ...metadata,
                          lastUpdated: new Date().toISOString()
                        }
                      }
                    };
                    
                    // Add to EntityStore
                    addEntity(entityWithMetadata);
                    
                    log.log(`✅ [doc_write] Fetched and added entity to EntityStore with metadata:`, {
                      entityId: fetchedEntity._id,
                      llmId: fetchedEntity.llmId,
                      name: fetchedEntity.name,
                      metadata
                    });
                  } else {
                    console.error(`❌ [doc_write] Could not fetch entity from backend: ${id}`);
                  }
                } catch (fetchError) {
                  console.error(`❌ [doc_write] Failed to fetch and add entity:`, fetchError);
                }
              }
            } else {
              console.warn(`⚠️ [doc_write] Failed to calculate metadata for ${doc.docId}`);
            }
          } catch (metadataError) {
            console.error(`❌ [doc_write] Failed to update EntityStore metadata:`, metadataError);
            // Don't throw - this is a non-critical optimization
          }
          
        } catch (error) {
          console.error(`❌ [TESTING] AI_EDIT_CONTEXT_REFRESH_FAILED:`, {
            operation: 'insert',
            llmId: id,
            docId: doc.docId,
            windowId: doc.windowId,
            keyUsedForRefresh: id,
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString(),
            phase: 'ai_edit_refresh_failed'
          });
        }
        
        // ✅ NEW: Export to markdown for showcase (with delay to ensure Y.Doc has settled)
        try {
          const { exportDocumentToMarkdown, shouldExportToMarkdown } = await import('../../utils/markdownExport');
          
          if (shouldExportToMarkdown(context)) {
            log.log('[doc_write] 📤 Scheduling markdown export for showcase (with 200ms delay)');
            
            // ✅ FIX: Delay export to ensure Y.Doc has fully settled after AI streaming
            // This prevents race conditions where we read Y.Doc while it's still being modified
            setTimeout(() => {
              log.log('[doc_write] 📤 Executing delayed markdown export');
              exportDocumentToMarkdown(doc.docId, doc.yDoc, {
                source: 'doc_write',
                blocking: false,
                verbose: true
              }).catch(err => {
                console.error('[doc_write] ❌ Delayed markdown export failed:', err);
              });
            }, 200); // 200ms delay to let Y.Doc settle
          } else {
            log.log('[doc_write] Skipping markdown export (not an AI operation)');
          }
        } catch (exportError) {
          console.error('[doc_write] Markdown export scheduling failed (non-critical):', exportError);
          // Don't throw - markdown export failure shouldn't break AI operation
        }
        
        try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...' }); } catch {}
        // 🔧 CRITICAL: Force flush to ensure persistence before workflow transitions
        await forceFlushDocumentSave(doc.docId);
        return success;
      }

      // Fallback (should not hit)
      traceService.log({ traceId: effectiveTraceId, type: 'tool_error', data: { tool: 'doc_write', message: 'Unhandled operation' } });
      return createErrorResult('Unhandled operation', 'Could not determine editing operation', 'system');
      
    } catch (error) {
      log.log('❌ [DOC_WRITE_HANDLER_DEBUG] === HANDLER EXCEPTION ===', {
        timestamp: new Date().toISOString(),
        toolUseId: context.executionId,
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : null,
        errorType: typeof error,
        executionTime: Date.now() - startTime
      });

      traceService.log({
        traceId: effectiveTraceId,
        type: 'tool_error',
        data: { tool: 'doc_write', error: error instanceof Error ? error.message : String(error) },
        metadata: { executionTime: Date.now() - startTime }
      });
      return createErrorResult('Write operation failed', error instanceof Error ? error.message : 'Unknown error', 'execution', { metadata: { executionTime: Date.now() - startTime } });
    }
  }
};

